(() => {
  if (customElements.get('rx-sunglasses-builder')) return;

  const privateState = new WeakMap();
  const moneyWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
    const number = (Number(value) || 0) / 100;
    const parts = number.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  const money = (amount, currency) => {
    const moneyFormat = document.querySelector('[data-money-format]')?.dataset.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
      const output = document.createElement('span');
      output.innerHTML = window.Shopify.formatMoney(Number(amount) || 0, moneyFormat);
      return output.textContent.trim();
    }
    if (moneyFormat) {
      const replacements = {
        amount: moneyWithDelimiters(amount),
        amount_no_decimals: moneyWithDelimiters(amount, 0),
        amount_with_comma_separator: moneyWithDelimiters(amount, 2, '.', ','),
        amount_no_decimals_with_comma_separator: moneyWithDelimiters(amount, 0, '.', ',')
      };
      const output = document.createElement('span');
      output.innerHTML = moneyFormat.replace(/\{\{\s*(amount(?:_no_decimals_with_comma_separator|_with_comma_separator|_no_decimals)?)\s*\}\}/, (_, key) => replacements[key] || replacements.amount);
      return output.textContent.trim();
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency', currency: currency || 'AUD'
    }).format((Number(amount) || 0) / 100);
  };
  const compactMoney = (amount, currency) => {
    const value = (Number(amount) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: currency || 'AUD', currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number(amount) % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
      }).format(value);
    } catch (_) {
      return `$${value.toFixed(Number(amount) % 100 === 0 ? 0 : 2)}`;
    }
  };
  const unique = (items) => [...new Set(items.filter(Boolean))];
  const escapeText = (value) => String(value ?? '');
  const PD_TEST_BUILD = '20260805-02';
  const directPdTestRequested = () => window.location.hash === '#pd-test'
    || new URLSearchParams(window.location.search).get('pd_test') === '1';
  const withPdTestBuild = (value) => {
    const url = new URL(value, window.location.href);
    url.searchParams.set('pd_build', PD_TEST_BUILD);
    return url.href;
  };
  const approvedSunTypeKeys = ['standard_tint', 'polarised', 'ultra_polarised'];
  const approvedThicknessKeys = ['standard_150', 'thin_160', 'extra_thin_167'];
  const multifocalDesignKeys = ['STD', 'WIDE', 'LARGEST'];
  const opticalSunTypeCodes = { standard_tint: 'TINT', polarised: 'POL', ultra_polarised: 'ULTRA' };
  const opticalThicknessCodes = { standard_150: 'T150', thin_160: 'T160', extra_thin_167: 'T167' };
  const approvedCombinationKeys = {
    'standard_tint:standard_150': 'tint_150',
    'standard_tint:thin_160': 'tint_160',
    'standard_tint:extra_thin_167': 'tint_167',
    'polarised:standard_150': 'polarised_150',
    'polarised:thin_160': 'polarised_160',
    'polarised:extra_thin_167': 'polarised_167',
    'ultra_polarised:standard_150': 'ultra_polarised_150',
    'ultra_polarised:thin_160': 'ultra_polarised_160',
    'ultra_polarised:extra_thin_167': 'ultra_polarised_167',
  };
  const approvedTintColours = ['Grey', 'Brown'];
  const otherTintChoice = 'Other / Not sure';
  const approvedTintChoices = [...approvedTintColours, otherTintChoice];
  const approvedAnalyticsEvents = new Set([
    'rx_validation_error', 'rx_upload_completed', 'rx_prescription_method_selected',
    'rx_pd_method_selected', 'rx_pd_later_selected', 'rx_pd_later_deselected', 'rx_pd_validation_error',
    'rx_sun_type_selected', 'rx_thickness_selected', 'rx_tint_colour_selected',
    'rx_tint_preview_changed', 'rx_carrera_preview_viewed', 'rx_carrera_upgrade_toggled', 'rx_assisted_order_clicked',
    'prescription_add_to_cart',
    'rx_builder_draft_saved', 'rx_builder_draft_resumed', 'rx_builder_draft_discarded',
    'rx_add_to_cart_started', 'rx_add_to_cart_completed', 'rx_add_to_cart_failed', 'rx_builder_reset_after_add',
    'rx_pd_assistance_clicked', 'pd_camera_preflight_viewed', 'pd_camera_started',
    'pd_alignment_issue_shown', 'pd_all_checks_ready', 'pd_measurement_completed',
    'pd_camera_cancelled', 'pd_manual_fallback_selected', 'pd_manual_fallback_automatic'
  ]);

  class RxSunglassesBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      const configNode = this.querySelector('[data-rx-config]');
      if (!configNode) return;
      let config;
      try { config = JSON.parse(configNode.textContent); } catch (_) { return; }

      const state = {
        config, step: 1, vision: '', multifocalDesign: '', multifocalAcknowledged: false,
        finish: '', tint: '', packageHandle: '', sunTypeKey: '', thicknessKey: '', carreraUpgrade: false,
        prescriptionMethod: '', prism: 'no', pdMethod: '', uploadProviderReady: false, uploadComplete: false,
        uploadInProgress: false, uploadError: false, uploadValue: '', clinical: {}, opener: null,
        completed: false, needsReview: false, addingToCart: false, variantId: 0,
        builderOpened: false, eligible: false, scrollLock: null, pdSource: '', pdProvenance: null,
        pdManualExpanded: false, previewVariantId: 0, previewTint: '', editingGroupId: '', suitabilityAccepted: false
      };
      privateState.set(this, state);
      this.dialog = this.querySelector('[data-rx-dialog]');
      this.form = document.getElementById(this.dataset.productForm);
      this.quantityInput = this.form?.querySelector('[name="quantity"]')
        || document.querySelector(`[name="quantity"][form="${CSS.escape(this.dataset.productForm)}"]`);
      this.populateManualPrescriptionSelects();
      this.bind();
      this.syncUploadState();
      this.updateClinicalDependencies();
      this.updateVariant(false);
      requestAnimationFrame(() => this.restoreCartEditFromLocation());
      requestAnimationFrame(() => this.openDirectPdTest());
      requestAnimationFrame(() => this.placePriceMatch());
      setTimeout(() => this.placePriceMatch(), 1000);
    }

    openDirectPdTest() {
      const state = privateState.get(this);
      const requested = directPdTestRequested();
      if (!requested || !state.eligible || !state.config.product.cameraPdDirectTestAvailable) return;
      state.vision = 'sv';
      state.step = 4;
      state.pdMethod = 'help';
      const help = this.querySelector('input[name^="rx-pd-method"][value="help"]');
      if (help) help.checked = true;
      this.open(this);
      requestAnimationFrame(() => this.openPdCamera(this.querySelector('[data-rx-pd-camera-open]')));
    }

    bind() {
      this.addEventListener('click', (event) => {
        const open = event.target.closest('[data-rx-open]');
        if (open) { event.preventDefault(); this.open(open); return; }
        if (event.target.closest('[data-rx-help-cta]')) this.track('rx_assisted_order_clicked');
        if (event.target.closest('[data-rx-pd-assistance]')) this.track('rx_pd_assistance_clicked');
        if (event.target.closest('[data-rx-close]')) this.close();
        if (event.target.closest('[data-rx-back]')) this.move(-1);
        if (event.target.closest('[data-rx-next]')) {
          if (privateState.get(this).step === 5) this.completeConfiguration();
          else this.move(1);
        }
        const editStep = event.target.closest('[data-rx-edit-step]');
        if (editStep) {
          const state = privateState.get(this); state.step = Number(editStep.dataset.rxEditStep) || 1;
          this.track(editStep.dataset.rxAnalytics || 'rx_review_edit'); this.render(true); return;
        }
        const pdHelpChoice = event.target.closest('[data-rx-pd-help-select]');
        if (pdHelpChoice) { event.preventDefault(); this.selectPdMethod(pdHelpChoice.dataset.rxPdHelpSelect); return; }
        const pdLaterChoice = event.target.closest('[data-rx-pd-later-select]');
        if (pdLaterChoice) { event.preventDefault(); this.selectPdLater(); return; }
        const pdCameraOpen = event.target.closest('[data-rx-pd-camera-open]');
        if (pdCameraOpen) { event.preventDefault(); this.openPdCamera(pdCameraOpen); return; }
        const pdManualOpen = event.target.closest('[data-rx-pd-manual-open]');
        if (pdManualOpen) {
          event.preventDefault();
          const state = privateState.get(this);
          if (state.pdManualExpanded) {
            state.pdManualExpanded = false;
            this.renderPd();
            requestAnimationFrame(() => pdManualOpen.focus());
          } else {
            this.showPdManualInstructions();
          }
          return;
        }
        const upgradePanel = event.target.closest('[data-rx-carrera-upgrade]');
        if (upgradePanel && !event.target.closest('[data-rx-carrera-upgrade-input], .rx-carrera-upgrade__control')) {
          const upgradeInput = this.querySelector('[data-rx-carrera-upgrade-input]');
          if (upgradeInput && !upgradeInput.disabled) { upgradeInput.click(); upgradeInput.focus(); }
        }
      });
      this.documentClickHandler = (event) => {
        const cartEdit = event.target.closest('[data-rx-cart-edit]');
        if (cartEdit && this.canOpenCartEditInline(cartEdit)) {
          event.preventDefault();
          this.openCartEditInline(cartEdit);
          return;
        }
        const open = event.target.closest('[data-rx-open]');
        if (open && !this.contains(open)) { event.preventDefault(); this.open(open); }
      };
      document.addEventListener('click', this.documentClickHandler);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      this.querySelectorAll('[data-rx-clinical]').forEach((input) => input.addEventListener('input', () => {
        const state = privateState.get(this);
        const wasInvalid = input.getAttribute('aria-invalid') === 'true';
        const isPdField = ['pd', 'pdRight', 'pdLeft'].includes(input.dataset.rxClinical);
        state.clinical[input.dataset.rxClinical] = input.value;
        if (isPdField) { state.pdSource = ''; state.pdProvenance = null; }
        if (wasInvalid && isPdField) this.validatePdField(input);
        else this.clearFieldError(input.dataset.rxClinical);
        this.markDirty(); this.updateClinicalDependencies(); this.error(''); this.updateForwardState();
      }));
      const suitabilityAcknowledgement = this.querySelector('[data-rx-suitability-acknowledgement]');
      suitabilityAcknowledgement?.addEventListener('change', () => {
        const state = privateState.get(this);
        state.suitabilityAccepted = suitabilityAcknowledgement.checked;
        suitabilityAcknowledgement.setAttribute('aria-invalid', String(!state.suitabilityAccepted));
        const suitabilityError = this.querySelector('[data-rx-suitability-error]');
        if (suitabilityError) suitabilityError.hidden = state.suitabilityAccepted;
        this.error('');
        this.updateForwardState();
      });
      this.querySelectorAll('[data-rx-vision]').forEach((input) => input.addEventListener('change', () => {
        const state = privateState.get(this);
        state.vision = input.value;
        if (state.vision === 'sv') {
          state.multifocalDesign = '';
          state.multifocalAcknowledged = false;
          delete state.clinical.odAdd;
          delete state.clinical.osAdd;
          this.querySelectorAll('[data-rx-clinical="odAdd"], [data-rx-clinical="osAdd"]').forEach((field) => { field.value = ''; });
          this.clearFieldError('odAdd'); this.clearFieldError('osAdd');
          const mfAcknowledgement = this.querySelector('[data-rx-mf-acknowledgement]');
          if (mfAcknowledgement) { mfAcknowledgement.checked = false; mfAcknowledgement.removeAttribute('aria-invalid'); }
        } else if (state.pdMethod === 'single') {
          state.pdMethod = 'dual';
          const dualPd = this.querySelector('input[name^="rx-pd-method"][value="dual"]');
          if (dualPd) dualPd.checked = true;
        }
        this.markDirty(); this.renderVision(); this.renderMethod(); this.renderPd(); this.updateForwardState();
      }));
      this.querySelectorAll('[data-rx-mf-design-input]').forEach((input) => input.addEventListener('change', () => {
        const state = privateState.get(this); state.multifocalDesign = input.value; this.markDirty(); this.renderVision(); this.updateForwardState();
      }));
      const multifocalAcknowledgement = this.querySelector('[data-rx-mf-acknowledgement]');
      multifocalAcknowledgement?.addEventListener('change', () => {
        const state = privateState.get(this); state.multifocalAcknowledged = multifocalAcknowledgement.checked;
        multifocalAcknowledgement.setAttribute('aria-invalid', String(!state.multifocalAcknowledged));
        const error = this.querySelector('[data-rx-mf-error]'); if (error) error.hidden = state.multifocalAcknowledged;
        this.error(''); this.updateForwardState();
      });
      this.querySelectorAll('[data-rx-clinical="pd"], [data-rx-clinical="pdRight"], [data-rx-clinical="pdLeft"]').forEach((input) => {
        input.addEventListener('blur', () => this.validatePdField(input));
      });
      this.querySelectorAll('input[name^="rx-prescription-method"]').forEach((input) => input.addEventListener('change', () => {
        const state = privateState.get(this); state.prescriptionMethod = input.value; this.markDirty();
        this.track('rx_prescription_method_selected'); if (input.value === 'email') this.track('rx_email_later_selected'); this.renderMethod(); this.clearFieldErrors(); this.updateForwardState();
      }));
      this.querySelectorAll('input[name^="rx-prism-"]').forEach((input) => input.addEventListener('change', () => {
        const state = privateState.get(this); state.prism = input.value; this.markDirty();
        this.renderMethod(); this.error(''); this.updateTotal(); this.updateForwardState();
      }));
      this.querySelectorAll('input[name^="rx-pd-method"]').forEach((input) => {
        input.addEventListener('keydown', (event) => { this.pdArrowNavigation = /^Arrow/.test(event.key); });
        input.addEventListener('keyup', () => { this.pdArrowNavigation = false; });
        input.addEventListener('blur', () => { this.pdArrowNavigation = false; });
        input.addEventListener('change', () => {
          const state = privateState.get(this);
          if (input.value === 'unknown') {
            if (!this.selectPdLater()) {
              input.checked = false;
              const previous = this.querySelector(`input[name^="rx-pd-method"][value="${state.pdMethod}"]`);
              if (previous) previous.checked = true;
            }
            return;
          }
          const wasAwaiting = state.pdMethod === 'unknown';
          state.pdMethod = input.value;
          state.pdSource = '';
          state.pdProvenance = null;
          if (input.value === 'help') state.pdManualExpanded = false;
          this.markDirty();
          if (wasAwaiting) this.track('rx_pd_later_deselected', { pd_method: input.value, pd_status: 'incomplete' });
          this.track('rx_pd_method_selected', { pd_method: input.value, pd_status: 'incomplete' });
          this.renderPd(); this.error(''); this.updateForwardState();
          if (this.pdArrowNavigation) return;
          const focusSelector = input.value === 'single'
            ? '[data-rx-clinical="pd"]'
            : input.value === 'dual'
              ? '[data-rx-clinical="pdRight"]'
              : '[data-rx-pd-help-heading]';
          requestAnimationFrame(() => {
            const focusTarget = this.querySelector(focusSelector);
            if (input.value === 'help' && window.matchMedia('(max-width: 749px)').matches) {
              focusTarget?.focus({ preventScroll: true });
              requestAnimationFrame(() => {
                const body = this.querySelector('[data-rx-dialog-body]');
                if (!body || !focusTarget) return;
                const bodyTop = body.getBoundingClientRect().top;
                const targetTop = focusTarget.getBoundingClientRect().top;
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                body.scrollTo({
                  top: Math.max(0, body.scrollTop + targetTop - bodyTop - 14),
                  behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });
              });
              return;
            }
            focusTarget?.focus();
          });
        });
      });
      this.pdMeasurementHandler = (event) => this.applyPdMeasurement(event.detail || {});
      this.pdManualHandler = () => this.showPdManualInstructions();
      this.pdAnalyticsHandler = (event) => {
        const allowed = ['pd_camera_preflight_viewed', 'pd_camera_started', 'pd_alignment_issue_shown', 'pd_all_checks_ready', 'pd_measurement_completed', 'pd_camera_cancelled', 'pd_manual_fallback_selected', 'pd_manual_fallback_automatic'];
        const issueCodes = ['face_missing', 'eyes_missing', 'head_pose', 'card_missing', 'card_partial', 'card_tilt', 'card_position', 'lighting', 'motion'];
        if (!allowed.includes(event.detail?.name)) return;
        const issueCode = issueCodes.includes(event.detail?.issueCode) ? event.detail.issueCode : '';
        this.track(event.detail.name, issueCode ? { error_code: issueCode } : {});
      };
      this.pdToolClosedHandler = (event) => {
        this.setPdToolModalState(false);
        if (event.detail?.reason === 'dismissed') requestAnimationFrame(() => this.pdOpener?.focus?.());
      };
      this.addEventListener('rx-pd-measurement-used', this.pdMeasurementHandler);
      this.addEventListener('rx-pd-manual-requested', this.pdManualHandler);
      this.addEventListener('rx-pd-analytics', this.pdAnalyticsHandler);
      this.addEventListener('rx-pd-tool-closed', this.pdToolClosedHandler);
      const carreraUpgradeInput = this.querySelector('[data-rx-carrera-upgrade-input]');
      carreraUpgradeInput?.addEventListener('change', () => {
        const state = privateState.get(this); state.carreraUpgrade = carreraUpgradeInput.checked && this.carreraUpgradeCompatibility().available;
        const compatibility = this.carreraUpgradeCompatibility();
        this.markDirty(); this.track('rx_carrera_upgrade_toggled', {
          sun_type_key: state.sunTypeKey,
          thickness_key: state.thicknessKey,
          combination_key: this.selectedCombination()?.key || '',
          carrera_eligibility: compatibility.available ? 'eligible' : 'ineligible',
          carrera_upgrade: state.carreraUpgrade ? 'yes' : 'no',
          engraving_type: this.carreraEngravingType()
        });
        this.showSelectionNotice(state.carreraUpgrade ? state.config.labels.carreraUpgradeAdded : state.config.labels.carreraUpgradeNotAdded);
        this.updateTotal(); this.renderCarreraUpgrade();
      });
      const useUpload = this.querySelector('[data-rx-use-upload]');
      useUpload?.addEventListener('click', () => {
        const upload = this.querySelector('input[name^="rx-prescription-method"][value="upload"]');
        if (!upload || upload.disabled) return;
        upload.checked = true; upload.dispatchEvent(new Event('change', { bubbles: true })); upload.focus();
      });
      this.formSubmitHandler = (event) => {
        if (event.submitter?.matches('[name="add"]')) this.track('non_prescription_add_to_cart');
      };
      this.formChangeHandler = () => setTimeout(() => this.updateVariant(true), 0);
      this.quantityInputHandler = () => this.syncPurchaseViews();
      this.mainButton = this.form?.querySelector('[name="add"]');
      this.mainButtonObserver = new MutationObserver(() => requestAnimationFrame(() => this.syncPurchaseViews()));
      if (this.mainButton) this.mainButtonObserver.observe(this.mainButton, { childList: true, subtree: true, characterData: true });
      this.form?.addEventListener('submit', this.formSubmitHandler, true);
      this.form?.addEventListener('change', this.formChangeHandler);
      this.quantityInput?.addEventListener('input', this.quantityInputHandler);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => this.updateVariant(true));
      }
      this.uploadSlot = this.querySelector('[data-rx-upload-slot]');
      this.uploadObserver = new MutationObserver(() => this.syncUploadState());
      if (this.uploadSlot) {
        this.uploadObserver.observe(this.uploadSlot, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['data-filepond-item-state', 'value', 'class']
        });
        this.uploadChangeHandler = () => this.syncUploadState();
        this.uploadSlot.addEventListener('change', this.uploadChangeHandler, true);
        this.uploadSlot.addEventListener('input', this.uploadChangeHandler, true);
      }
    }

    canOpenCartEditInline(link) {
      const state = privateState.get(this);
      if (!link || !state?.eligible || !link.dataset.rxCartEditGroup) return false;
      try {
        return new URL(link.href, window.location.href).pathname === window.location.pathname;
      } catch (_) {
        return false;
      }
    }

    async openCartEditInline(link) {
      const groupId = link?.dataset?.rxCartEditGroup;
      if (!groupId || link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      document.querySelector('cart-drawer')?.close?.();
      try {
        await this.restoreCartGroup(groupId);
        const opener = document.querySelector('[data-rx-entry-button]') || this;
        this.open(opener);
      } catch (_) {
        const message = privateState.get(this)?.config?.labels?.cartEditError || 'We could not reopen these prescription sunglasses. Please refresh and try again.';
        this.error(message);
      } finally {
        link.removeAttribute('aria-busy');
      }
    }

    placePriceMatch() {
      const slot = (this.closest('.product__info-container') || document).querySelector('[data-rx-price-match-slot]');
      const priceMatch = (this.closest('.product__info-container') || document).querySelector('[id*="mega_better_price_app_block"]');
      if (slot && priceMatch && priceMatch.parentElement !== slot) slot.append(priceMatch);
    }

    currentVariant() {
      const state = privateState.get(this);
      const id = Number(this.form?.querySelector('[name="id"]')?.value || 0);
      return state.config.variants.find((variant) => Number(variant.id) === id) || state.config.variants[0];
    }

    prescriptionVariant() {
      const state = privateState.get(this); const selected = this.currentVariant();
      if (selected?.status === 'unavailable') return null;
      const colourIndex = Number(state.config.product.colourOptionIndex) || 0;
      const lensIndex = Number(state.config.product.lensOptionIndex) || 0;
      const colour = selected?.options?.[colourIndex];
      const selectedLens = String(selected?.options?.[lensIndex] || '');
      const customPolarisedSelected = this.form?.dataset.cityCustomPolarisedSelected === 'true'
        || /custom\s+polari[sz]ed/i.test(selectedLens);
      if (!customPolarisedSelected) return selected?.available ? selected : null;
      const standardVariant = state.config.variants.find((variant) => variant.options?.[colourIndex] === colour
        && variant.options?.[lensIndex] === 'Standard' && variant.available && variant.status !== 'unavailable');
      return standardVariant || null;
    }

    setStickyBuyLabel(button, fullLabel, total, available = true) {
      const desktop = button.querySelector('[data-sunglass-sticky-buy-desktop]');
      const mobile = button.querySelector('[data-sunglass-sticky-buy-mobile]');
      if (!desktop || !mobile) { button.textContent = fullLabel; return; }
      desktop.textContent = fullLabel;
      mobile.textContent = available
        ? `Buy as shown · ${compactMoney(total, privateState.get(this).config.product.currency)}`
        : fullLabel;
    }

    setStickyPrescriptionLabel(button, fullLabel, total) {
      const desktop = button.querySelector('[data-sunglass-sticky-rx-desktop]');
      const mobile = button.querySelector('[data-sunglass-sticky-rx-mobile]');
      if (!desktop || !mobile) { button.textContent = fullLabel; return; }
      desktop.textContent = fullLabel;
      mobile.innerHTML = `<span>Prescription lenses</span><small>from ${compactMoney(total, privateState.get(this).config.product.currency)}</small>`;
    }

    resetCustomPolarisedForPrescription(opener) {
      if (this.form?.dataset.cityCustomPolarisedSelected !== 'true') return false;
      if (this.dataset.cityRxResetting === 'true') return true;
      const container = this.closest('.product__info-container') || document;
      const lensGroup = container.querySelector('[data-city-sun-option-role="lens"]');
      const standard = Array.from(lensGroup?.querySelectorAll('input[type="radio"]') || [])
        .find((input) => /^standard$/i.test(input.value) && !input.disabled);
      if (!standard) {
        let unavailableNotice = document.querySelector('[data-city-rx-reset-notice]');
        if (!unavailableNotice) {
          unavailableNotice = document.createElement('div');
          unavailableNotice.dataset.cityRxResetNotice = 'true';
          unavailableNotice.className = 'city-rx-reset-notice';
          unavailableNotice.setAttribute('role', 'alert');
          document.body.appendChild(unavailableNotice);
        }
        unavailableNotice.textContent = 'Prescription lenses are unavailable for this colour because a matching Standard frame package is not available.';
        unavailableNotice.classList.add('is-visible');
        window.clearTimeout(this.rxResetNoticeTimer);
        this.rxResetNoticeTimer = window.setTimeout(() => unavailableNotice.classList.remove('is-visible'), 5200);
        return true;
      }
      this.dataset.cityRxResetting = 'true';
      standard.click();
      let notice = document.querySelector('[data-city-rx-reset-notice]');
      if (!notice) {
        notice = document.createElement('div');
        notice.dataset.cityRxResetNotice = 'true';
        notice.className = 'city-rx-reset-notice';
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        document.body.appendChild(notice);
      }
      notice.textContent = 'Custom polarised upgrade removed. Choose your prescription lens options in the next step.';
      notice.classList.add('is-visible');
      window.clearTimeout(this.rxResetNoticeTimer);
      this.rxResetNoticeTimer = window.setTimeout(() => notice.classList.remove('is-visible'), 4200);
      window.setTimeout(() => {
        delete this.dataset.cityRxResetting;
        this.updateVariant(true);
        this.open(opener);
      }, 120);
      return true;
    }

    track(eventName, details = {}) {
      const state = privateState.get(this); const selectedVariant = this.currentVariant(); const variant = this.prescriptionVariant();
      if (!state || !approvedAnalyticsEvents.has(eventName) || !Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({
        event: eventName,
        product_id: String(state.config.product.id || ''),
        variant_id: String(selectedVariant?.id || ''),
        eligibility_status: variant?.status || '',
        completion_status: state.completed ? 'complete' : 'incomplete',
        ...(details.error_code ? { error_code: details.error_code } : {}),
        ...(details.pd_method ? { pd_method: details.pd_method } : {}),
        ...(details.pd_status ? { pd_status: details.pd_status } : {}),
        ...(details.lens_colour ? { lens_colour: details.lens_colour } : {}),
        ...(details.package_key ? { package_key: details.package_key } : {}),
        ...(details.sun_type_key ? { sun_type_key: details.sun_type_key } : {}),
        ...(details.thickness_key ? { thickness_key: details.thickness_key } : {}),
        ...(details.combination_key ? { combination_key: details.combination_key } : {}),
        ...(details.carrera_eligibility ? { carrera_eligibility: details.carrera_eligibility } : {}),
        ...(details.carrera_upgrade ? { carrera_upgrade: details.carrera_upgrade } : {}),
        ...(details.engraving_type ? { engraving_type: details.engraving_type } : {})
      });
    }

    validSunTypes() {
      return (this.prescriptionVariant()?.configuration?.sunTypes || privateState.get(this)?.config?.configuration?.sunTypes || [])
        .filter((item) => item.active === true
          && approvedSunTypeKeys.includes(item.key)
          && Number.isFinite(Number(item.basePrice))
          && Number(item.basePrice) >= 0)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    }

    validThicknesses() {
      return (this.prescriptionVariant()?.configuration?.thicknesses || privateState.get(this)?.config?.configuration?.thicknesses || [])
        .filter((item) => item.active === true
          && approvedThicknessKeys.includes(item.key)
          && Number.isFinite(Number(item.surcharge))
          && Number(item.surcharge) >= 0)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    }

    validCombinations() {
      return (this.prescriptionVariant()?.configuration?.combinations || privateState.get(this)?.config?.configuration?.combinations || [])
        .filter((item) => item.active === true
          && item.serviceAvailable === true
          && Number(item.serviceVariantId) > 0
          && Number.isFinite(Number(item.servicePrice))
          && Number(item.servicePrice) >= 0
          && approvedSunTypeKeys.includes(item.sunTypeKey)
          && approvedThicknessKeys.includes(item.thicknessKey)
          && Array.isArray(item.colours) && item.colours.length > 0)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    }

    hasApprovedConfiguration() {
      const sunTypes = this.validSunTypes();
      const thicknesses = this.validThicknesses();
      const combinations = this.validCombinations();
      const keys = new Set(combinations.map((item) => `${item.sunTypeKey}:${item.thicknessKey}`));
      const combinationKeys = new Set(combinations.map((item) => item.key));
      const sunTypeKeys = new Set(sunTypes.map((item) => item.key));
      const thicknessKeys = new Set(thicknesses.map((item) => item.key));
      const serviceVariantIds = new Set(combinations.map((item) => Number(item.serviceVariantId)));
      const sunTypePrices = new Map(sunTypes.map((item) => [item.key, Number(item.basePrice)]));
      const thicknessPrices = new Map(thicknesses.map((item) => [item.key, Number(item.surcharge)]));
      const pricesMatchComponents = combinations.every((item) => Number(item.servicePrice)
        === Number(sunTypePrices.get(item.sunTypeKey)) + Number(thicknessPrices.get(item.thicknessKey)));
      const combinationContractsMatch = combinations.every((item) => {
        const pairKey = `${item.sunTypeKey}:${item.thicknessKey}`;
        const colours = new Set(item.colours);
        return item.key === approvedCombinationKeys[pairKey]
          && colours.size === approvedTintColours.length
          && approvedTintColours.every((colour) => colours.has(colour));
      });
      const configuration = this.prescriptionVariant()?.configuration || privateState.get(this)?.config?.configuration;
      return configuration?.active === true
        && sunTypes.length === approvedSunTypeKeys.length
        && sunTypeKeys.size === approvedSunTypeKeys.length
        && approvedSunTypeKeys.every((key) => sunTypeKeys.has(key))
        && thicknesses.length === approvedThicknessKeys.length
        && thicknessKeys.size === approvedThicknessKeys.length
        && approvedThicknessKeys.every((key) => thicknessKeys.has(key))
        && combinations.length === approvedSunTypeKeys.length * approvedThicknessKeys.length
        && combinationKeys.size === combinations.length
        && serviceVariantIds.size === combinations.length
        && pricesMatchComponents
        && combinationContractsMatch
        && approvedSunTypeKeys.every((sunTypeKey) => approvedThicknessKeys.every((thicknessKey) => keys.has(`${sunTypeKey}:${thicknessKey}`)))
        && this.hasMultifocalConfiguration();
    }

    opticalServiceSku(design = privateState.get(this).multifocalDesign || 'STD', sunTypeKey = privateState.get(this).sunTypeKey, thicknessKey = privateState.get(this).thicknessKey) {
      const sunCode = opticalSunTypeCodes[sunTypeKey]; const thicknessCode = opticalThicknessCodes[thicknessKey];
      return multifocalDesignKeys.includes(design) && sunCode && thicknessCode
        ? `CO-OPT-MF-${design}-SUN-${thicknessCode}-${sunCode}`
        : '';
    }

    opticalService(design = privateState.get(this).multifocalDesign || 'STD', sunTypeKey, thicknessKey) {
      const state = privateState.get(this); const sku = this.opticalServiceSku(design, sunTypeKey, thicknessKey);
      const service = sku ? state.config.opticalServices?.[sku] : null;
      return service?.available === true && Number(service.id) > 0 && Number.isFinite(Number(service.price))
        ? { ...service, sku }
        : null;
    }

    hasMultifocalConfiguration() {
      return multifocalDesignKeys.every((design) => approvedSunTypeKeys.every((sunTypeKey) => approvedThicknessKeys.every((thicknessKey) => {
        const service = this.opticalService(design, sunTypeKey, thicknessKey);
        return service && Number(service.price) >= 0;
      })));
    }

    selectedService() {
      const state = privateState.get(this);
      if (state.vision === 'mf') return this.opticalService(state.multifocalDesign || 'STD');
      return this.selectedCombination();
    }

    multifocalStandardService() { return this.opticalService('STD'); }
    multifocalBaseUpgrade() {
      const standard = this.multifocalStandardService(); const singleVision = this.selectedCombination();
      return standard && singleVision ? Math.max(0, Number(standard.price) - Number(singleVision.servicePrice)) : 0;
    }
    multifocalDesignUpgrade() {
      const state = privateState.get(this); const standard = this.multifocalStandardService(); const selected = this.opticalService(state.multifocalDesign || 'STD');
      return standard && selected ? Math.max(0, Number(selected.price) - Number(standard.price)) : 0;
    }
    visionLabel() { return privateState.get(this).vision === 'mf' ? 'Multifocal' : 'Single Vision'; }
    multifocalDesignLabel() { return ({ STD: 'Standard Design', WIDE: 'Comfort Design', LARGEST: 'Ultimate Design' })[privateState.get(this).multifocalDesign] || ''; }

    carreraUpgradeCompatibility() {
      const state = privateState.get(this); const config = this.prescriptionVariant()?.carreraUpgrade || {};
      const carreraVendor = state.config.product.vendor === 'Carrera';
      const configured = carreraVendor && config.configured === true && config.designFixture !== true
        && config.verifiedAssetsReady === true
        && config.serviceAvailable === true && Number(config.serviceVariantId) > 0 && Number(config.servicePrice) >= 0;
      const tints = Array.isArray(config.tints) ? config.tints.filter(Boolean) : [];
      const combinationCompatible = this.selectedCombination()?.carreraCompatible === true;
      const selectedTint = privateState.get(this).tint;
      const tintCompatible = selectedTint === otherTintChoice || (tints.length > 0 && tints.includes(selectedTint));
      return { config, available: configured && combinationCompatible && tintCompatible, configured };
    }

    carreraEngravingType() {
      return privateState.get(this).sunTypeKey === 'standard_tint' ? 'uv' : 'polarised';
    }

    upgradePrice() {
      const state = privateState.get(this); const compatibility = this.carreraUpgradeCompatibility();
      return state.carreraUpgrade && compatibility.available ? Number(compatibility.config.servicePrice) || 0 : 0;
    }

    totalAmount() {
      const variant = this.prescriptionVariant(); const service = this.selectedService();
      return Number(variant?.price || 0) + Number(service?.price ?? service?.servicePrice ?? 0) + this.upgradePrice() + this.prismPrice();
    }

    prismPrice() {
      const state = privateState.get(this);
      return state.prism === 'yes' ? Number(state.config.product.prismUpgrade?.price || 5000) : 0;
    }

    prismValidationIssue() {
      const state = privateState.get(this); const c = state.clinical || {};
      if (!state.prism) return { message: state.config.labels.prismRequired || 'Choose whether your prescription includes prism.', selector: 'input[name^="rx-prism-"]', errorCode: 'prism_required' };
      if (state.prism !== 'yes') return null;
      const upgrade = state.config.product.prismUpgrade || {};
      if (!upgrade.available || !Number(upgrade.id)) return { message: 'Prism treatment is temporarily unavailable online. Please contact City Optics for help.', selector: 'input[name^="rx-prism-"][value="yes"]', errorCode: 'prism_unavailable' };
      if (state.prescriptionMethod !== 'manual') return null;
      const pairs = [
        ['odPrismHorizontal', 'odPrismHorizontalBase'], ['odPrismVertical', 'odPrismVerticalBase'],
        ['osPrismHorizontal', 'osPrismHorizontalBase'], ['osPrismVertical', 'osPrismVerticalBase']
      ];
      let complete = 0;
      for (const [amountKey, baseKey] of pairs) {
        const amount = String(c[amountKey] || '').trim(); const base = String(c[baseKey] || '').trim();
        if (!amount && !base) continue;
        const number = Number(amount);
        if (!Number.isFinite(number) || number < .25 || number > 20 || Math.abs(number * 4 - Math.round(number * 4)) > .001 || !base) {
          return { message: 'Enter each prism amount from 0.25Δ to 20.00Δ in 0.25 steps and choose its BASE direction.', selector: `[data-rx-clinical="${amount ? baseKey : amountKey}"]`, errorCode: 'prism_values_invalid' };
        }
        complete += 1;
      }
      return complete ? null : { message: state.config.labels.prismValuesRequired || 'Enter at least one complete prism amount and BASE direction, or choose No prism.', selector: '[data-rx-prism-fields] input', errorCode: 'prism_values_required' };
    }

    uploadPropertyValue() {
      const state = privateState.get(this); const slot = this.uploadSlot;
      if (!state || !slot) return state?.uploadValue || '';
      const field = [...slot.querySelectorAll('input, textarea')].find((candidate) => {
        const value = String(candidate.value || '');
        return candidate.type !== 'file' && this.isUploadReference(value);
      });
      const value = String(field?.value || state.uploadValue || '');
      return this.isUploadReference(value) ? value : '';
    }

    isUploadReference(value) {
      try {
        const url = new URL(String(value || ''));
        return url.protocol === 'https:' && (url.hostname === 'cloudlift.app' || url.hostname.endsWith('.cloudlift.app'));
      } catch (_) {
        return false;
      }
    }

    clinicalCartProperties() {
      const state = privateState.get(this); const clinical = state.clinical || {}; const properties = {};
      if (state.pdSource === 'camera') throw new Error('camera_pd_validation_required');
      if (state.prescriptionMethod === 'upload') {
        const uploadValue = this.uploadPropertyValue();
        if (!uploadValue) throw new Error('prescription_upload_missing');
        properties.Prescription = 'Uploaded';
        properties['_Prescription Upload'] = uploadValue;
      } else if (state.prescriptionMethod === 'manual') {
        properties.Prescription = 'Entered manually';
        const labels = {
          odSph: '_Right Eye / OD SPH', odCyl: '_Right Eye / OD CYL', odAxis: '_Right Eye / OD AXIS',
          osSph: '_Left Eye / OS SPH', osCyl: '_Left Eye / OS CYL', osAxis: '_Left Eye / OS AXIS',
          ...(state.vision === 'mf' ? { odAdd: '_Right Eye / OD ADD', osAdd: '_Left Eye / OS ADD' } : {})
        };
        Object.entries(labels).forEach(([key, label]) => {
          const value = String(clinical[key] || '').trim();
          if (value) properties[label] = value;
        });
        if (state.prism === 'yes') {
          const prismLabels = {
            odPrismHorizontal: '_Right Eye / OD Horizontal Prism', odPrismHorizontalBase: '_Right Eye / OD Horizontal Base',
            odPrismVertical: '_Right Eye / OD Vertical Prism', odPrismVerticalBase: '_Right Eye / OD Vertical Base',
            osPrismHorizontal: '_Left Eye / OS Horizontal Prism', osPrismHorizontalBase: '_Left Eye / OS Horizontal Base',
            osPrismVertical: '_Left Eye / OS Vertical Prism', osPrismVerticalBase: '_Left Eye / OS Vertical Base'
          };
          Object.entries(prismLabels).forEach(([key, label]) => {
            const value = String(clinical[key] || '').trim();
            if (value) properties[label] = value;
          });
        }
      } else {
        properties.Prescription = 'To be emailed';
      }
      if (state.pdMethod === 'unknown') {
        properties.PD = 'To be provided';
      } else {
        properties.PD = 'Provided';
        if (state.pdMethod === 'single' && String(clinical.pd || '').trim()) properties._PD = String(clinical.pd).trim();
        if (state.pdMethod === 'dual') {
          if (String(clinical.pdRight || '').trim()) properties['_Right PD'] = String(clinical.pdRight).trim();
          if (String(clinical.pdLeft || '').trim()) properties['_Left PD'] = String(clinical.pdLeft).trim();
        }
      }
      return properties;
    }

    buildCartLineItems() {
      const state = privateState.get(this);
      const frameVariant = this.prescriptionVariant();
      const combination = this.selectedCombination();
      const selectedService = this.selectedService();
      const sunType = this.selectedSunType();
      const thickness = this.selectedThickness();
      const compatibility = this.carreraUpgradeCompatibility();
      if (!state.config.product.cartEnabled || !state.eligible || !this.hasApprovedConfiguration()
        || !frameVariant?.available || !combination || !selectedService || selectedService.available === false
        || !['sv', 'mf'].includes(state.vision)
        || (state.vision === 'mf' && (!state.multifocalDesign || !state.multifocalAcknowledged))) {
        throw new Error('configuration_unavailable');
      }
      const quantity = 1;
      const groupId = globalThis.crypto?.randomUUID?.() || `rxs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const previewImage = this.previewSource(frameVariant)?.url || frameVariant.image || '';
      const sharedProperties = {
        '_city_rx_sunglasses_group': groupId,
        '_city_rx_sunglasses_configuration': state.vision === 'mf' ? selectedService.sku : combination.key,
        '_city_rx_sunglasses_frame_variant': String(frameVariant.id),
        '_city_rx_preview_image': previewImage
      };
      const lensIndex = Number(state.config.product.lensOptionIndex) || 0;
      const suppliedLensOption = String(frameVariant?.options?.[lensIndex] || '');
      const suppliedOriginalLenses = /original.*polari[sz]ed/i.test(suppliedLensOption);
      const serviceProperties = {
        ...sharedProperties,
        '_city_rx_sunglasses_service': 'true',
        '_city_rx_restore_version': '1',
        '_city_rx_prescription_method': state.prescriptionMethod,
        '_city_rx_pd_method': state.pdMethod,
        'Vision type': this.visionLabel(),
        ...(state.vision === 'mf' ? {
          'Multifocal design': this.multifocalDesignLabel(),
          'Multifocal fitting photo': 'Required after checkout',
          'Fitting photo status': 'Awaiting customer photo',
          'Fitting photo acknowledgement': state.multifocalAcknowledged ? 'Confirmed' : 'Not confirmed',
          '_Optical Review Status': 'Hold — fitting photo pending',
          '_Production Ready': 'No — optical review required',
          '_Staff Action': 'Email customer for front-facing fitting photo; approve or request retake before production'
        } : {
          '_Optical Review Status': 'Pending optical review',
          '_Production Ready': 'No — optical review required',
          '_Staff Action': 'Review prescription, PD and frame suitability before production'
        }),
        '_Lens type': sunType?.title || '',
        '_Lens thickness': [thickness?.title, thickness?.technical].filter(Boolean).join(' '),
        'Lens colour': state.tint,
        'Prism': state.prism === 'yes' ? 'Yes — prism treatment required' : 'No',
        ...this.clinicalCartProperties(),
        ...(state.config.product.suitabilityReviewRequired ? {
          '_city_rx_suitability_review': 'required',
          'Optical suitability review': state.config.labels.suitabilityReviewProperty || 'Required before production'
        } : {}),
        ...(state.tint === otherTintChoice ? { '_city_rx_colour_confirmation': 'required' } : {})
      };
      const items = [
        {
          id: Number(frameVariant.id),
          quantity,
          properties: {
            ...sharedProperties,
            '_city_rx_sunglasses_parent': 'true',
            ...(frameVariant.frameCode ? { 'Frame code': frameVariant.frameCode } : {}),
            ...(suppliedOriginalLenses ? {
              'Supplied spare lenses': suppliedLensOption.replace(/polarized/gi, 'Polarised'),
              'Include removed original lenses': 'Yes'
            } : {})
          }
        },
        {
          id: Number(selectedService.id || selectedService.serviceVariantId),
          quantity,
          properties: serviceProperties
        }
      ];
      if (state.carreraUpgrade) {
        if (!compatibility.available) throw new Error('configuration_unavailable');
        items.push({
          id: Number(compatibility.config.serviceVariantId),
          quantity,
          properties: {
            ...sharedProperties,
            '_city_rx_sunglasses_upgrade': 'true',
            'Upgrade': state.config.labels.carreraUpgradeLabel || 'Genuine Carrera lens upgrade'
          }
        });
      }
      if (state.prism === 'yes') {
        const prismUpgrade = state.config.product.prismUpgrade || {};
        if (!prismUpgrade.available || !Number(prismUpgrade.id)) throw new Error('configuration_unavailable');
        items.push({
          id: Number(prismUpgrade.id),
          quantity,
          properties: {
            ...sharedProperties,
            '_city_rx_prism_upgrade': 'true',
            'Prism treatment': 'One pair'
          }
        });
      }
      return { items, groupId };
    }

    cartItemProperty(item, key) {
      const properties = item?.properties;
      if (Array.isArray(properties)) return properties.find((property) => property?.name === key)?.value || '';
      return properties && typeof properties === 'object' ? properties[key] || '' : '';
    }

    async restoreCartGroup(groupId) {
      const state = privateState.get(this); const root = window.Shopify?.routes?.root || '/';
      const request = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!request.ok) throw new Error('cart_edit_load_failed');
      const cart = await request.json();
      const grouped = (cart.items || []).filter((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId);
      const frame = grouped.find((item) => this.cartItemProperty(item, '_city_rx_sunglasses_parent') === 'true');
      const service = grouped.find((item) => this.cartItemProperty(item, '_city_rx_sunglasses_service') === 'true');
      const upgrade = grouped.find((item) => this.cartItemProperty(item, '_city_rx_sunglasses_upgrade') === 'true');
      const prism = grouped.find((item) => this.cartItemProperty(item, '_city_rx_prism_upgrade') === 'true');
      if (!frame || !service || grouped.length !== 2 + Number(Boolean(upgrade)) + Number(Boolean(prism))) throw new Error('cart_edit_group_invalid');

      const frameVariantId = Number(this.cartItemProperty(service, '_city_rx_sunglasses_frame_variant') || frame.variant_id || frame.id);
      if (Number(this.prescriptionVariant()?.id) !== frameVariantId) throw new Error('cart_edit_variant_mismatch');
      const combinationKey = this.cartItemProperty(service, '_city_rx_sunglasses_configuration');
      const multifocalMatch = /^CO-OPT-MF-(STD|WIDE|LARGEST)-SUN-(T150|T160|T167)-(TINT|POL|ULTRA)$/.exec(combinationKey);
      const sunTypeKey = multifocalMatch ? Object.keys(opticalSunTypeCodes).find((key) => opticalSunTypeCodes[key] === multifocalMatch[3]) : '';
      const thicknessKey = multifocalMatch ? Object.keys(opticalThicknessCodes).find((key) => opticalThicknessCodes[key] === multifocalMatch[2]) : '';
      const combination = this.validCombinations().find((item) => multifocalMatch
        ? item.sunTypeKey === sunTypeKey && item.thicknessKey === thicknessKey
        : item.key === combinationKey);
      if (!combination) throw new Error('cart_edit_configuration_invalid');

      state.vision = multifocalMatch ? 'mf' : 'sv';
      state.multifocalDesign = multifocalMatch?.[1] || '';
      state.multifocalAcknowledged = multifocalMatch ? this.cartItemProperty(service, 'Fitting photo acknowledgement') === 'Confirmed' : false;
      state.sunTypeKey = combination.sunTypeKey;
      state.thicknessKey = combination.thicknessKey;
      state.packageHandle = combination.sunTypeKey;
      state.finish = combination.thicknessKey;
      state.tint = this.cartItemProperty(service, 'Lens colour');
      state.carreraUpgrade = Boolean(upgrade);
      state.prism = prism ? 'yes' : (this.cartItemProperty(service, 'Prism') === 'Yes — prism treatment required' ? 'yes' : 'no');
      state.prescriptionMethod = this.cartItemProperty(service, '_city_rx_prescription_method');
      state.pdMethod = this.cartItemProperty(service, '_city_rx_pd_method');
      state.uploadValue = this.cartItemProperty(service, '_Prescription Upload');
      state.uploadComplete = Boolean(state.uploadValue);
      state.clinical = {
        odSph: this.cartItemProperty(service, '_Right Eye / OD SPH'),
        odCyl: this.cartItemProperty(service, '_Right Eye / OD CYL'),
        odAxis: this.cartItemProperty(service, '_Right Eye / OD AXIS'),
        osSph: this.cartItemProperty(service, '_Left Eye / OS SPH'),
        osCyl: this.cartItemProperty(service, '_Left Eye / OS CYL'),
        osAxis: this.cartItemProperty(service, '_Left Eye / OS AXIS'),
        odAdd: this.cartItemProperty(service, '_Right Eye / OD ADD'),
        osAdd: this.cartItemProperty(service, '_Left Eye / OS ADD'),
        odPrismHorizontal: this.cartItemProperty(service, '_Right Eye / OD Horizontal Prism'),
        odPrismHorizontalBase: this.cartItemProperty(service, '_Right Eye / OD Horizontal Base'),
        odPrismVertical: this.cartItemProperty(service, '_Right Eye / OD Vertical Prism'),
        odPrismVerticalBase: this.cartItemProperty(service, '_Right Eye / OD Vertical Base'),
        osPrismHorizontal: this.cartItemProperty(service, '_Left Eye / OS Horizontal Prism'),
        osPrismHorizontalBase: this.cartItemProperty(service, '_Left Eye / OS Horizontal Base'),
        osPrismVertical: this.cartItemProperty(service, '_Left Eye / OS Vertical Prism'),
        osPrismVerticalBase: this.cartItemProperty(service, '_Left Eye / OS Vertical Base'),
        pd: this.cartItemProperty(service, '_PD'),
        pdRight: this.cartItemProperty(service, '_Right PD'),
        pdLeft: this.cartItemProperty(service, '_Left PD')
      };
      state.editingGroupId = groupId;
      state.step = 5;
      state.completed = false;
      state.needsReview = false;
      state.builderOpened = false;
      state.suitabilityAccepted = false;

      this.querySelectorAll('[data-rx-vision]').forEach((input) => { input.checked = input.value === state.vision; });
      this.querySelectorAll('[data-rx-mf-design-input]').forEach((input) => { input.checked = input.value === state.multifocalDesign; });
      this.querySelectorAll('input[name^="rx-prescription-method"]').forEach((input) => { input.checked = input.value === state.prescriptionMethod; });
      this.querySelectorAll('input[name^="rx-prism-"]').forEach((input) => { input.checked = input.value === state.prism; });
      this.querySelectorAll('input[name^="rx-pd-method"]').forEach((input) => { input.checked = input.value === state.pdMethod; });
      this.querySelectorAll('[data-rx-clinical]').forEach((input) => { input.value = state.clinical[input.dataset.rxClinical] || ''; });
      const upgradeInput = this.querySelector('[data-rx-carrera-upgrade-input]');
      if (upgradeInput) upgradeInput.checked = state.carreraUpgrade;
      const mfAcknowledgement = this.querySelector('[data-rx-mf-acknowledgement]');
      if (mfAcknowledgement) mfAcknowledgement.checked = state.multifocalAcknowledged;
      this.updateClinicalDependencies();
      this.clearFieldErrors();
      this.renderVision();
      this.renderOptions();
      return true;
    }

    async restoreCartEditFromLocation() {
      const url = new URL(window.location.href); const groupId = url.searchParams.get('rx_edit');
      if (!groupId || !privateState.get(this)?.eligible) return;
      try {
        await this.restoreCartGroup(groupId);
        url.searchParams.delete('rx_edit');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        document.querySelector('cart-drawer')?.close?.();
        this.open(this);
      } catch (_) {
        const message = privateState.get(this)?.config?.labels?.cartEditError || 'We could not reopen these prescription sunglasses. Please refresh and try again.';
        this.error(message);
      }
    }

    verifyAddedGroup(response, items, groupId) {
      const added = Array.isArray(response?.items) ? response.items : [];
      if (added.length !== items.length) return false;
      const grouped = added.filter((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId);
      if (grouped.length !== items.length) return false;
      const expectedIds = items.map((item) => Number(item.id)).sort((a, b) => a - b);
      const addedIds = grouped.map((item) => Number(item.variant_id || item.id)).sort((a, b) => a - b);
      if (expectedIds.some((id, index) => id !== addedIds[index])) return false;
      const markerCount = (marker) => grouped.filter((item) => this.cartItemProperty(item, marker) === 'true').length;
      return markerCount('_city_rx_sunglasses_parent') === 1
        && markerCount('_city_rx_sunglasses_service') === 1
        && markerCount('_city_rx_sunglasses_upgrade') === (privateState.get(this).carreraUpgrade ? 1 : 0)
        && markerCount('_city_rx_prism_upgrade') === (privateState.get(this).prism === 'yes' ? 1 : 0)
        && grouped.every((item) => Boolean(this.cartItemProperty(item, '_city_rx_sunglasses_configuration')));
    }

    async rollbackCartGroup(groupId) {
      if (!groupId) return;
      const root = window.Shopify?.routes?.root || '/';
      const cartRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!cartRequest.ok) throw new Error('cart_rollback_load_failed');
      const cart = await cartRequest.json();
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId && item.key) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST', cache: 'no-store', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('cart_rollback_failed');
      const verifyRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!verifyRequest.ok) throw new Error('cart_rollback_verify_failed');
      const verifyCart = await verifyRequest.json();
      if ((verifyCart.items || []).some((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId)) {
        throw new Error('cart_rollback_incomplete');
      }
    }

    cartGroupMatchesSnapshot(items, snapshot, groupId) {
      const grouped = (items || []).filter((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId);
      if (grouped.length !== snapshot.length) return false;
      const actualIds = grouped.map((item) => Number(item.variant_id || item.id)).sort((a, b) => a - b);
      const expectedIds = snapshot.map((item) => Number(item.id)).sort((a, b) => a - b);
      return actualIds.every((id, index) => id === expectedIds[index]);
    }

    async restoreCartGroupSnapshot(groupId, snapshot) {
      const root = window.Shopify?.routes?.root || '/';
      const currentRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!currentRequest.ok) throw new Error('cart_edit_recovery_failed');
      const currentCart = await currentRequest.json(); const updates = {};
      (currentCart.items || []).forEach((item) => {
        if (this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId && item.key) updates[item.key] = 0;
      });
      if (Object.keys(updates).length) {
        const removeResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST', cache: 'no-store', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates })
        });
        if (!removeResponse.ok) throw new Error('cart_edit_recovery_failed');
      }
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST', cache: 'no-store', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: snapshot })
      });
      if (!addResponse.ok) throw new Error('cart_edit_recovery_failed');
      const verifyRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!verifyRequest.ok) throw new Error('cart_edit_recovery_failed');
      const verifyCart = await verifyRequest.json();
      if (!this.cartGroupMatchesSnapshot(verifyCart.items, snapshot, groupId)) throw new Error('cart_edit_recovery_failed');
    }

    async removeCartGroup(groupId, sectionIds = []) {
      if (!groupId) throw new Error('cart_edit_group_missing');
      const root = window.Shopify?.routes?.root || '/';
      const cartRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!cartRequest.ok) throw new Error('cart_edit_load_failed');
      const cart = await cartRequest.json(); const updates = {};
      const originalGroup = (cart.items || []).filter((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId);
      const snapshot = originalGroup.map((item) => ({
        id: Number(item.variant_id || item.id),
        quantity: Number(item.quantity) || 1,
        properties: item.properties || {}
      }));
      originalGroup.forEach((item) => {
        if (this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId && item.key) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) throw new Error('cart_edit_group_missing');
      let response; let result;
      try {
        response = await fetch(`${root}cart/update.js`, {
          method: 'POST', cache: 'no-store', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            updates,
            ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {})
          })
        });
        result = await response.json().catch(() => ({}));
      } catch (_) {
        result = {};
      }
      const verifyRequest = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!verifyRequest.ok) throw new Error('cart_edit_recovery_failed');
      const verifyCart = await verifyRequest.json();
      const remaining = (verifyCart.items || []).filter((item) => this.cartItemProperty(item, '_city_rx_sunglasses_group') === groupId);
      if (!remaining.length) return response?.ok && !result.status ? result : verifyCart;
      if (!this.cartGroupMatchesSnapshot(verifyCart.items, snapshot, groupId)) {
        await this.restoreCartGroupSnapshot(groupId, snapshot);
      }
      throw new Error('cart_edit_remove_failed');
    }

    focusAddedCartGroup(groupId) {
      const row = groupId ? document.querySelector(`[data-city-rx-group-id="${CSS.escape(groupId)}"]`) : null;
      const target = row?.querySelector('.city-rx-cart-group')
        || row
        || document.querySelector('#CartDrawer .drawer__heading, #CartDrawer [role="heading"]');
      if (target && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(target.tagName) && !target.hasAttribute('tabindex')) target.tabIndex = -1;
      target?.focus?.({ preventScroll: false });
    }

    async refreshCartAfterAdd(response, groupId) {
      const drawer = document.querySelector('cart-drawer');
      if (window.CityCartEdit && typeof window.CityCartEdit.refreshCartDrawer === 'function') {
        try {
          await window.CityCartEdit.refreshCartDrawer(true, 1);
          setTimeout(() => this.focusAddedCartGroup(groupId), 700);
          return;
        } catch (_) {}
      }
      if (drawer && typeof drawer.renderContents === 'function' && response?.sections) {
        drawer.renderContents(response);
        requestAnimationFrame(() => this.focusAddedCartGroup(groupId));
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'rx-sunglasses' } }));
      drawer?.open?.();
      requestAnimationFrame(() => this.focusAddedCartGroup(groupId));
    }

    async addPrescriptionToCart() {
      const state = privateState.get(this);
      if (state.addingToCart) return;
      state.addingToCart = true;
      this.error('');
      this.render(false);
      let items;
      let groupId = '';
      let response;
      const editingGroupId = state.editingGroupId;
      try {
        ({ items, groupId } = this.buildCartLineItems());
        this.track('rx_add_to_cart_started', {
          sun_type_key: state.sunTypeKey,
          thickness_key: state.thicknessKey,
          combination_key: this.selectedCombination()?.key || ''
        });
        const drawer = document.querySelector('cart-drawer');
        const sectionIds = typeof drawer?.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id).filter(Boolean)
          : ['cart-drawer', 'cart-icon-bubble'];
        const root = window.Shopify?.routes?.root || '/';
        const cartAddUrl = window.routes?.cart_add_url || `${root}cart/add.js`;
        const request = await fetch(cartAddUrl, {
          method: 'POST',
          cache: 'no-store',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });
        response = await request.json().catch(() => ({}));
        if (!request.ok || response.status) throw new Error(response.description || response.message || 'cart_add_failed');
        if (!this.verifyAddedGroup(response, items, groupId)) throw new Error('cart_add_incomplete');
        if (editingGroupId) response = await this.removeCartGroup(editingGroupId, sectionIds);
      } catch (error) {
        let recoveryFailed = error?.message === 'cart_edit_recovery_failed';
        if (groupId) {
          try { await this.rollbackCartGroup(groupId); } catch (_) { recoveryFailed = true; }
        }
        state.addingToCart = false;
        const unavailable = error?.message === 'configuration_unavailable';
        const message = unavailable
          ? state.config.labels.cartConfigurationUnavailable
          : (recoveryFailed
              ? state.config.labels.cartRecoveryError
              : (editingGroupId ? state.config.labels.cartUpdateError : state.config.labels.cartAddError));
        this.track('rx_add_to_cart_failed', { error_code: unavailable ? 'configuration_unavailable' : (error?.message === 'cart_add_incomplete' ? 'cart_add_incomplete' : 'cart_add_failed') });
        this.render(false);
        this.error(message);
        return;
      }
      this.track('rx_add_to_cart_completed', {
        sun_type_key: state.sunTypeKey,
        thickness_key: state.thicknessKey,
        combination_key: this.selectedCombination()?.key || '',
        lens_colour: state.tint,
        carrera_upgrade: state.carreraUpgrade ? 'yes' : 'no'
      });
      this.close({ restoreFocus: false });
      this.resetDraft({ afterAdd: true });
      this.refreshCartAfterAdd(response, groupId).catch(() => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'rx-sunglasses' } }));
        document.querySelector('cart-drawer')?.open?.();
      });
    }

    updateVariant(changed) {
      const state = privateState.get(this);
      const selectedVariant = this.currentVariant(); const variant = this.prescriptionVariant();
      if (!selectedVariant) return;
      if (!variant) {
        state.variantId = Number(selectedVariant.id);
        state.eligible = false;
        if (state.completed || state.builderOpened) {
          state.completed = false;
          state.needsReview = true;
          if (this.dialog.open) this.close();
        }
        document.querySelectorAll('[data-rx-open]').forEach((button) => {
          if (this.contains(button)) return;
          button.hidden = true;
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        });
        const assist = this.querySelector('[data-rx-assist]');
        if (assist) { assist.hidden = true; assist.textContent = ''; }
        this.renderPurchaseState();
        this.syncPurchaseViews();
        return;
      }
      const previousVariantId = state.variantId;
      const variantChanged = previousVariantId > 0 && Number(selectedVariant.id) !== Number(previousVariantId);
      state.variantId = Number(selectedVariant.id);
      const eligible = state.config.product.prescriptionEnabled === true
        && Boolean(variant?.available)
        && this.hasApprovedConfiguration();
      state.eligible = eligible;
      const combinations = this.validCombinations();
      const lowestPrice = combinations.length ? Math.min(...combinations.map((item) => Number(item.servicePrice))) : 0;
      const prescriptionStartingTotal = Number(variant?.price || 0) + lowestPrice;
      if (!eligible && (state.completed || state.builderOpened)) {
        state.completed = false;
        state.needsReview = true;
        if (this.dialog.open) this.close();
      }
      const assist = this.querySelector('[data-rx-assist]');
      if (assist) { assist.hidden = true; assist.textContent = ''; }
      document.querySelectorAll('[data-rx-open]').forEach((button) => {
        if (this.contains(button)) return;
        button.hidden = !eligible;
        button.disabled = !eligible;
        button.setAttribute('aria-disabled', String(!eligible));
        if (button.matches('[data-rx-edit]')) {
          button.textContent = state.config.labels.edit;
          button.setAttribute('aria-label', state.config.labels.edit);
        } else if (button.matches('[data-rx-entry-button]')) {
          if (eligible) {
            button.textContent = this.prescriptionStartingLabel(prescriptionStartingTotal);
            button.setAttribute('aria-label', (state.config.labels.buildPrescriptionSunglassesAccessible || 'Build prescription sunglasses, starting total [price]').replace('[price]', money(prescriptionStartingTotal, state.config.product.currency)));
          }
        } else if (button.matches('[data-rx-sticky-prescription]')) {
          if (eligible) {
            const label = state.builderOpened ? state.config.labels.completePrescription : this.prescriptionStartingLabel(prescriptionStartingTotal);
            this.setStickyPrescriptionLabel(button, label, prescriptionStartingTotal);
            button.setAttribute('aria-label', label);
          }
        } else if (button.matches('[data-rx-section-cta]')) {
          if (eligible) {
            button.textContent = this.prescriptionSectionCtaLabel(prescriptionStartingTotal);
            button.setAttribute('aria-label', button.textContent);
          }
        } else {
          if (eligible) button.textContent = state.config.labels.configure;
        }
      });
      const sunTypeInvalid = Boolean(state.sunTypeKey) && !this.validSunTypes().some((item) => item.key === state.sunTypeKey);
      const thicknessInvalid = Boolean(state.thicknessKey) && !this.validThicknesses().some((item) => item.key === state.thicknessKey);
      const retainedCombination = combinations.find((item) => item.sunTypeKey === state.sunTypeKey && item.thicknessKey === state.thicknessKey);
      const packageInvalid = Boolean(state.sunTypeKey && state.thicknessKey) && !retainedCombination;
      const tints = unique(combinations.flatMap((item) => item.colours || []));
      const tintInvalid = Boolean(state.tint) && state.tint !== otherTintChoice && !tints.includes(state.tint);
      if (sunTypeInvalid || packageInvalid) state.sunTypeKey = '';
      if (thicknessInvalid || packageInvalid) state.thicknessKey = '';
      state.packageHandle = state.sunTypeKey;
      state.finish = state.thicknessKey;
      if (tintInvalid) state.tint = '';
      const upgradeInvalid = state.carreraUpgrade && !this.carreraUpgradeCompatibility().available;
      if (upgradeInvalid) state.carreraUpgrade = false;
      if (variantChanged && (packageInvalid || sunTypeInvalid || thicknessInvalid || tintInvalid || upgradeInvalid)) {
        state.completed = false;
        state.needsReview = true;
        state.step = 1;
      }
      this.renderOptions();
      this.renderVision();
      this.updateDialogHeader();
      this.renderPurchaseState();
      if (this.dialog.open && variantChanged && (packageInvalid || sunTypeInvalid || thicknessInvalid || tintInvalid || upgradeInvalid)) this.render(true);
      this.syncPurchaseViews();
    }

    open(opener) {
      const state = privateState.get(this);
      if (!state.eligible) return;
      if (this.resetCustomPolarisedForPrescription(opener)) return;
      if (state.addingToCart && !this.dialog.open) this.resetDraft({ afterAdd: true });
      const resuming = state.builderOpened;
      state.builderOpened = true;
      state.opener = opener || document.activeElement;
      if (resuming) this.track('rx_builder_draft_resumed');
      this.render();
      if (!this.dialog.open) this.dialog.showModal();
      this.lockPageScroll();
      requestAnimationFrame(() => this.focusCurrentStep());
    }

    close({ restoreFocus = true } = {}) {
      const state = privateState.get(this);
      this.pdOpenToken = (this.pdOpenToken || 0) + 1;
      const wasOpen = this.dialog.open;
      const opener = state.opener;
      if (this.pdController && !this.querySelector('[data-rx-pd-tool]')?.hidden) this.pdController.close(false);
      else this.setPdToolModalState(false);
      if (wasOpen) this.dialog.close();
      if (wasOpen && state.builderOpened && !state.addingToCart) this.track('rx_builder_draft_saved');
      this.unlockPageScroll();
      if (wasOpen && restoreFocus) requestAnimationFrame(() => {
        const fallback = document.querySelector('[data-rx-edit]:not([hidden]):not([disabled]), [data-rx-open]:not([hidden]):not([disabled])');
        const target = !opener?.disabled && opener?.getClientRects?.().length ? opener : fallback;
        target?.focus?.();
      });
    }

    lockPageScroll() {
      const state = privateState.get(this); const body = document.body; const root = document.documentElement;
      if (state.scrollLock) return;
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      state.scrollLock = {
        scrollY: window.scrollY,
        paddingRight: body.style.paddingRight,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width
      };
      if (scrollbarWidth > 0) {
        const currentPadding = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
        body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
      }
      body.style.position = 'fixed';
      body.style.top = `-${state.scrollLock.scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.classList.add('rx-dialog-open');
      root.classList.add('rx-dialog-open');
    }

    unlockPageScroll() {
      const state = privateState.get(this); const body = document.body; const root = document.documentElement;
      body.classList.remove('rx-dialog-open');
      root.classList.remove('rx-dialog-open');
      if (!state.scrollLock) return;
      body.style.paddingRight = state.scrollLock.paddingRight;
      body.style.position = state.scrollLock.position;
      body.style.top = state.scrollLock.top;
      body.style.left = state.scrollLock.left;
      body.style.right = state.scrollLock.right;
      body.style.width = state.scrollLock.width;
      const scrollY = state.scrollLock.scrollY;
      state.scrollLock = null;
      window.scrollTo(0, scrollY);
    }

    trapFocus(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) { event.preventDefault(); this.querySelector('[data-rx-close]')?.focus(); return; }
      const first = focusable[0]; const last = focusable[focusable.length - 1]; const active = document.activeElement;
      if (event.shiftKey && (active === first || !this.dialog.contains(active))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) { event.preventDefault(); first.focus(); }
    }

    stepName(step = privateState.get(this).step) {
      const state = privateState.get(this);
      return state.config.labels.stepNames?.[step - 1]
        || this.querySelector(`[data-rx-step="${step}"] [data-rx-step-title]`)?.textContent?.trim()
        || '';
    }

    selectedColour(variant = this.currentVariant()) {
      const state = privateState.get(this); const colourIndex = Number(state.config.product.colourOptionIndex) || 0;
      return String(variant?.options?.[colourIndex] || '').trim();
    }

    previewSource(variant = this.currentVariant()) {
      const state = privateState.get(this);
      const tint = String(state.tint || '').toLowerCase();
      const wantsLensPreview = tint === 'grey' || tint === 'brown';
      const preview = tint === 'grey' || tint === 'brown' ? variant?.previews?.[tint] : '';
      return {
        url: preview || (!wantsLensPreview ? variant?.image || '' : ''),
        fallbackUrl: variant?.image || '',
        isLensPreview: Boolean(preview),
        missing: wantsLensPreview && !preview,
        tint
      };
    }

    previewAlt(variant, tint, isLensPreview) {
      const state = privateState.get(this);
      const product = state.config.product.title;
      const colour = this.selectedColour(variant);
      if (isLensPreview && tint) {
        return state.config.labels.previewAlt
          .replace('[product]', product)
          .replace('[colour]', colour)
          .replace('[tint]', tint);
      }
      return [product, colour].filter(Boolean).join(' · ');
    }

    preloadVariantPreviews(variant = this.currentVariant()) {
      const state = privateState.get(this);
      if (state.step !== 2 || !variant) return;
      const token = (this.previewPreloadToken || 0) + 1;
      this.previewPreloadToken = token;
      this.previewPreloads = ['grey', 'brown'].map((tint) => {
        const url = variant.previews?.[tint];
        if (!url) return null;
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        image.onload = image.onerror = () => {
          if (token !== this.previewPreloadToken) return;
        };
        return image;
      }).filter(Boolean);
    }

    updatePreviewImage() {
      const state = privateState.get(this); const variant = this.currentVariant();
      const media = this.querySelector('[data-rx-context-media]');
      const active = this.querySelector('[data-rx-context-image]');
      const incoming = this.querySelector('[data-rx-context-image-next]');
      const label = this.querySelector('[data-rx-preview-label]');
      const status = this.querySelector('[data-rx-preview-status]');
      if (!media || !active || !variant) return;

      const swapToken = (this.previewSwapToken || 0) + 1;
      this.previewSwapToken = swapToken;
      media.classList.remove('is-crossfading');
      if (incoming) {
        incoming.hidden = true;
        incoming.removeAttribute('src');
        incoming.alt = '';
      }

      const source = this.previewSource(variant);
      const variantKey = String(variant.id || '');
      const displayUrl = source.url || (
        source.missing && active.dataset.rxVariantId !== variantKey ? source.fallbackUrl : ''
      );
      const displayIsLensPreview = source.isLensPreview && displayUrl === source.url;
      const tintLabel = source.tint ? `${source.tint[0].toUpperCase()}${source.tint.slice(1)}` : '';
      const alt = this.previewAlt(variant, source.tint, displayIsLensPreview);
      const commitMetadata = (lensPreview, unavailable) => {
        if (label) {
          label.textContent = lensPreview ? state.config.labels.previewLabel.replace('[tint]', tintLabel) : '';
          label.hidden = !lensPreview;
        }
        if (!status) return;
        status.textContent = unavailable ? state.config.labels.previewUnavailable : '';
        status.hidden = !unavailable;
      };

      media.hidden = !displayUrl && !active.currentSrc && !active.src;
      if (source.missing) {
        commitMetadata(false, true);
      }
      if (!displayUrl) return;
      if (active.dataset.rxSource === displayUrl) {
        active.alt = alt;
        active.dataset.rxVariantId = variantKey;
        commitMetadata(displayIsLensPreview, source.missing);
        return;
      }

      const loader = new Image();
      loader.decoding = 'async';
      loader.onload = () => {
        if (swapToken !== this.previewSwapToken) return;
        if (!active.dataset.rxSource || !incoming) {
          active.src = displayUrl;
          active.dataset.rxSource = displayUrl;
          active.dataset.rxVariantId = variantKey;
          active.alt = alt;
          commitMetadata(displayIsLensPreview, source.missing);
          return;
        }
        incoming.src = displayUrl;
        incoming.alt = '';
        incoming.hidden = false;
        requestAnimationFrame(() => {
          if (swapToken !== this.previewSwapToken) return;
          media.classList.add('is-crossfading');
          window.setTimeout(() => {
            if (swapToken !== this.previewSwapToken) return;
            active.src = displayUrl;
            active.dataset.rxSource = displayUrl;
            active.dataset.rxVariantId = variantKey;
            active.alt = alt;
            media.classList.remove('is-crossfading');
            incoming.hidden = true;
            incoming.removeAttribute('src');
            incoming.alt = '';
            commitMetadata(displayIsLensPreview, source.missing);
          }, 220);
        });
      };
      loader.onerror = () => {
        if (swapToken !== this.previewSwapToken) return;
        commitMetadata(false, true);
      };
      loader.src = displayUrl;
      this.preloadVariantPreviews(variant);
    }

    updateDialogHeader() {
      const state = privateState.get(this); const labels = state.config.labels; const variant = this.currentVariant();
      const stepText = labels.stepOf.replace('[current]', state.step); const stepName = this.stepName();
      const progress = this.querySelector('[data-rx-progress]'); if (progress) progress.textContent = stepText;
      const stepNameNode = this.querySelector('[data-rx-step-name]'); if (stepNameNode) stepNameNode.textContent = stepName;
      const context = this.querySelector('[data-rx-product-context]');
      if (context) context.textContent = [state.config.product.title, this.selectedColour(variant)].filter(Boolean).join(' · ');
      const frameCode = this.querySelector('[data-rx-frame-code]');
      if (frameCode) { frameCode.textContent = variant?.frameCode ? `${labels.frameCode}: ${variant.frameCode}` : ''; frameCode.hidden = !variant?.frameCode; }
      this.updatePreviewImage();
      const progressbar = this.querySelector('[data-rx-progressbar]');
      if (progressbar) {
        progressbar.setAttribute('aria-valuenow', String(state.step));
        progressbar.setAttribute('aria-valuetext', `${stepText}: ${stepName}`);
        progressbar.style.setProperty('--rx-progress', `${(state.step / 5) * 100}%`);
      }
      this.querySelectorAll('[data-rx-progress-segment]').forEach((segment, index) => {
        segment.classList.toggle('is-reached', index < state.step);
        segment.classList.toggle('is-current', index === state.step - 1);
      });
    }

    focusCurrentStep() {
      const state = privateState.get(this); const body = this.querySelector('[data-rx-dialog-body]');
      if (body) body.scrollTop = 0;
      const title = this.querySelector(`[data-rx-step="${state.step}"] [data-rx-step-title]`);
      (title || this.querySelector('[data-rx-close]'))?.focus();
    }

    discard() {
      const state = privateState.get(this);
      if (this.hasPrivateData() && !window.confirm(state.config.labels.discardConfirm)) return;
      this.track('rx_builder_draft_discarded');
      state.builderOpened = false;
      this.close();
      this.resetDraft();
    }

    hasPrivateData() {
      const state = privateState.get(this);
      return state.uploadComplete
        || this.pdController?.hasValidationData?.()
        || Object.values(state.clinical || {}).some((value) => String(value || '').trim() !== '');
    }

    removeConfiguration() {
      this.discard();
    }

    resetDraft({ afterAdd = false } = {}) {
      const state = privateState.get(this);
      state.step = 1; state.vision = ''; state.multifocalDesign = ''; state.multifocalAcknowledged = false;
      state.finish = ''; state.tint = ''; state.packageHandle = ''; state.sunTypeKey = ''; state.thicknessKey = ''; state.carreraUpgrade = false;
      state.prescriptionMethod = ''; state.prism = 'no'; state.pdMethod = ''; state.uploadComplete = false; state.uploadInProgress = false; state.uploadError = false; state.uploadValue = ''; state.clinical = {};
      state.pdSource = ''; state.pdProvenance = null; state.pdManualExpanded = false;
      state.completed = false; state.needsReview = false; state.builderOpened = false; state.addingToCart = false; state.editingGroupId = ''; state.suitabilityAccepted = false;
      this.pdController?.clearValidationData?.(false);
      this.querySelectorAll('[data-rx-clinical]').forEach((input) => { input.value = ''; });
      this.querySelectorAll('input[name^="rx-vision"], input[name^="rx-mf-design"], input[name^="rx-prescription-method"], input[name^="rx-prism-"], input[name^="rx-pd-method"]').forEach((input) => { input.checked = input.name.startsWith('rx-prism-') && input.value === 'no'; });
      const mfAcknowledgement = this.querySelector('[data-rx-mf-acknowledgement]');
      if (mfAcknowledgement) { mfAcknowledgement.checked = false; mfAcknowledgement.removeAttribute('aria-invalid'); }
      const mfError = this.querySelector('[data-rx-mf-error]'); if (mfError) mfError.hidden = true;
      const suitabilityAcknowledgement = this.querySelector('[data-rx-suitability-acknowledgement]');
      if (suitabilityAcknowledgement) { suitabilityAcknowledgement.checked = false; suitabilityAcknowledgement.removeAttribute('aria-invalid'); }
      const suitabilityError = this.querySelector('[data-rx-suitability-error]'); if (suitabilityError) suitabilityError.hidden = true;
      const upgrade = this.querySelector('[data-rx-carrera-upgrade-input]'); if (upgrade) upgrade.checked = false;
      this.clearUploadState({ preserveRemote: afterAdd }); this.clearFieldErrors(); this.error(''); this.renderOptions(); this.renderVision(); this.renderPurchaseState(); this.syncPurchaseViews();
      if (afterAdd) this.track('rx_builder_reset_after_add');
    }

    updateClinicalDependencies() {
      const state = privateState.get(this);
      [['odCyl', 'odAxis'], ['osCyl', 'osAxis']].forEach(([cylKey, axisKey]) => {
        const cyl = String(state.clinical[cylKey] || '').trim(); const axis = this.querySelector(`[data-rx-clinical="${axisKey}"]`);
        const needsAxis = cyl !== '' && Number(cyl) !== 0;
        axis.disabled = !needsAxis;
        if (!needsAxis) { axis.value = ''; delete state.clinical[axisKey]; this.clearFieldError(axisKey); }
      });
      this.querySelectorAll('[data-rx-prism-amount]').forEach((amount) => {
        const amountKey = amount.dataset.rxPrismAmount;
        const baseKey = `${amountKey}Base`;
        const base = this.querySelector(`[data-rx-prism-base="${amountKey}"]`);
        const hasPrism = String(state.clinical[amountKey] || '').trim() !== '';
        if (!base) return;
        base.disabled = !hasPrism;
        if (!hasPrism) { base.value = ''; delete state.clinical[baseKey]; this.clearFieldError(baseKey); }
      });
    }

    populateManualPrescriptionSelects() {
      const formatSignedQuarter = (value) => value === 0 ? '0.00' : `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
      const buildRange = (start, end, step) => {
        const values = [];
        if (start <= end) {
          for (let value = start; value <= end + 0.001; value += step) values.push(Number(value.toFixed(2)));
        } else {
          for (let value = start; value >= end - 0.001; value -= step) values.push(Number(value.toFixed(2)));
        }
        return values;
      };
      const ranges = {
        sph: buildRange(-12, 8, 0.25).map(formatSignedQuarter),
        cyl: buildRange(-0.25, -6, 0.25).map(formatSignedQuarter),
        axis: buildRange(1, 180, 1).map(String),
        add: buildRange(0.25, 5, 0.25).map(formatSignedQuarter)
      };
      this.querySelectorAll('[data-rx-manual-field]').forEach((select) => {
        if (select.options.length > 1) return;
        (ranges[select.dataset.rxManualField] || []).forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        });
      });
      this.querySelectorAll('[data-rx-prism-amount]').forEach((select) => {
        if (select.options.length > 1) return;
        buildRange(0.25, 20, 0.25).forEach((value) => {
          const option = document.createElement('option');
          option.value = value.toFixed(2);
          option.textContent = `${value.toFixed(2)} Δ`;
          select.append(option);
        });
      });
    }

    renderCard(container, name, value, title, copy, price, checked) {
      const label = document.createElement('label'); label.className = 'rx-choice';
      const input = document.createElement('input'); input.type = 'radio'; input.name = name; input.value = value; input.checked = checked;
      const span = document.createElement('span'); const strong = document.createElement('strong'); strong.textContent = title;
      span.append(strong);
      if (copy) { const small = document.createElement('small'); small.textContent = copy; span.append(small); }
      if (price) { const em = document.createElement('em'); em.textContent = price; span.append(em); }
      label.append(input, span); container.append(label); return input;
    }

    selectedSunType() { const state = privateState.get(this); return this.validSunTypes().find((item) => item.key === state.sunTypeKey); }
    selectedThickness() { const state = privateState.get(this); return this.validThicknesses().find((item) => item.key === state.thicknessKey); }
    selectedCombination() {
      const state = privateState.get(this);
      return this.validCombinations().find((item) => item.sunTypeKey === state.sunTypeKey && item.thicknessKey === state.thicknessKey);
    }

    packageTitle() {
      const sunType = this.selectedSunType(); const thickness = this.selectedThickness();
      return [sunType?.title, thickness ? `${thickness.title} ${thickness.technical}` : ''].filter(Boolean).join(' / ');
    }

    thicknessCardContent(item) {
      const labels = privateState.get(this).config.labels;
      const content = {
        standard_150: {
          title: labels.thicknessStandardTitle,
          description: labels.thicknessStandardDescription,
          technical: labels.thicknessStandardRange,
          badge: ''
        },
        thin_160: {
          title: labels.thicknessThinTitle,
          description: labels.thicknessThinDescription,
          technical: labels.thicknessThinRange,
          badge: labels.packageMostPopular
        },
        extra_thin_167: {
          title: labels.thicknessExtraTitle,
          description: labels.thicknessExtraDescription,
          technical: labels.thicknessExtraRange,
          badge: ''
        }
      }[item.key];
      return content ? { ...item, ...content } : item;
    }

    sunTypeCardContent(item) {
      const labels = privateState.get(this).config.labels;
      const badge = {
        standard_tint: '',
        polarised: labels.packageMostPopular,
        ultra_polarised: labels.packageBestSunglassPerformance
      }[item.key];
      return badge === undefined ? item : { ...item, badge };
    }

    renderPackageCard(container, item, checked, kind = 'sunType') {
      const state = privateState.get(this);
      const isThickness = kind === 'thickness';
      const displayItem = isThickness ? this.thicknessCardContent(item) : this.sunTypeCardContent(item);
      const inputId = `Rx-${kind}-${this.dataset.sectionId}-${item.key}`.replace(/[^a-zA-Z0-9_-]/g, '-');
      const descriptionId = `${inputId}-description`; const technicalId = `${inputId}-technical`; const priceId = `${inputId}-price`;
      const badgeId = displayItem.badge ? `${inputId}-badge` : '';
      const label = document.createElement('label'); label.className = `rx-package-card${isThickness ? ' rx-package-card--thickness' : ''}`; label.dataset.rxChoiceKey = item.key;
      const input = document.createElement('input'); input.className = 'rx-package-card__input'; input.type = 'radio'; input.id = inputId;
      input.name = `rx-${kind}-${this.dataset.sectionId}`; input.value = item.key; input.checked = checked;
      input.setAttribute('aria-label', displayItem.title); input.setAttribute('aria-describedby', [badgeId, descriptionId, technicalId, priceId].filter(Boolean).join(' '));
      const indicator = document.createElement('span'); indicator.className = 'rx-package-card__indicator'; indicator.setAttribute('aria-hidden', 'true');
      label.append(input, indicator);
      if (displayItem.badge) {
        const badge = document.createElement('span'); badge.className = `rx-package-card__badge${!isThickness && item.key === 'ultra_polarised' ? ' rx-package-card__badge--performance' : ''}`; badge.id = badgeId; badge.textContent = displayItem.badge; label.append(badge);
      }
      const graphic = document.createElement('span'); graphic.className = 'rx-package-card__graphic';
      const icon = isThickness ? state.config.product.thicknessIcons?.[item.key] : state.config.product.sunTypeIcons?.[item.key];
      if (icon) { const image = document.createElement('img'); image.src = icon; image.alt = ''; graphic.append(image); }
      const title = document.createElement('span'); title.className = 'rx-package-card__title'; title.textContent = displayItem.title;
      const description = document.createElement('span'); description.className = 'rx-package-card__description'; description.id = descriptionId; description.textContent = displayItem.description || '';
      const technical = document.createElement('span'); technical.className = 'rx-package-card__technical'; technical.id = technicalId; technical.textContent = displayItem.technical || '';
      const amount = Number(isThickness ? item.surcharge : item.basePrice) || 0;
      const price = document.createElement('span'); price.className = 'rx-package-card__price'; price.id = priceId;
      price.textContent = isThickness ? (amount === 0 ? 'Included' : `+ ${money(amount, state.config.product.currency)}`) : money(amount, state.config.product.currency);
      label.append(graphic, title, description, technical, price); container.append(label); return input;
    }

    renderTintCard(container, value, checked) {
      const state = privateState.get(this); const labels = state.config.labels;
      const inputId = `RxTint-${this.dataset.sectionId}-${value}`.replace(/[^a-zA-Z0-9_-]/g, '-');
      const descriptionId = `${inputId}-description`;
      const badgeText = value === 'Grey' ? labels.tintMostPopular : '';
      const badgeId = badgeText ? `${inputId}-badge` : '';
      const label = document.createElement('label'); label.className = 'rx-tint-card';
      const input = document.createElement('input'); input.type = 'radio'; input.id = inputId; input.name = `rx-tint-${this.dataset.sectionId}`; input.value = value; input.checked = checked;
      input.setAttribute('aria-describedby', [badgeId, descriptionId].filter(Boolean).join(' '));
      const swatch = document.createElement('span'); swatch.className = 'rx-tint-card__swatch'; swatch.setAttribute('aria-hidden', 'true');
      const icon = state.config.product.tintIcons?.[value];
      if (icon) { const image = document.createElement('img'); image.src = icon; image.alt = ''; swatch.append(image); }
      const copy = document.createElement('span'); copy.className = 'rx-tint-card__copy';
      const title = document.createElement('strong'); title.textContent = value;
      const description = document.createElement('small'); description.id = descriptionId;
      description.textContent = value === 'Grey'
        ? labels.tintGreyDescription
        : value === 'Brown'
          ? labels.tintBrownDescription
          : labels.tintOtherDescription;
      const indicator = document.createElement('span'); indicator.className = 'rx-tint-card__indicator'; indicator.setAttribute('aria-hidden', 'true');
      label.append(input);
      if (badgeText) {
        const badge = document.createElement('span'); badge.className = 'rx-tint-card__badge'; badge.id = badgeId; badge.textContent = badgeText; label.append(badge);
      }
      copy.append(title, description); label.append(swatch, copy, indicator); container.append(label); return input;
    }

    renderCarreraUpgrade() {
      const state = privateState.get(this); const compatibility = this.carreraUpgradeCompatibility();
      const panel = this.querySelector('[data-rx-carrera-upgrade]'); const input = this.querySelector('[data-rx-carrera-upgrade-input]');
      const price = this.querySelector('[data-rx-carrera-upgrade-price]'); const reason = this.querySelector('[data-rx-carrera-upgrade-reason]');
      if (!panel || !input) return;
      panel.hidden = !compatibility.available;
      if (!compatibility.configured) { state.carreraUpgrade = false; input.checked = false; input.disabled = true; return; }
      panel.dataset.available = String(compatibility.available);
      input.disabled = !compatibility.available;
      if (!compatibility.available) state.carreraUpgrade = false;
      input.checked = state.carreraUpgrade;
      if (price) price.textContent = `+ ${money(compatibility.config.servicePrice, state.config.product.currency)}`;
      if (reason) {
        reason.hidden = true;
        reason.textContent = '';
      }
    }

    showSelectionNotice(message) {
      const notice = this.querySelector('[data-rx-selection-notice]');
      if (!notice) return;
      notice.textContent = message || '';
      notice.hidden = !message;
    }

    markDirty() {
      this.renderPurchaseState(); this.syncPurchaseViews();
    }

    renderVision() {
      const state = privateState.get(this);
      this.querySelectorAll('[data-rx-vision]').forEach((input) => { input.checked = input.value === state.vision; });
      this.querySelectorAll('[data-rx-mf-design-input]').forEach((input) => { input.checked = input.value === state.multifocalDesign; });
      const designFieldset = this.querySelector('[data-rx-mf-design]');
      if (designFieldset) { designFieldset.hidden = state.vision !== 'mf'; designFieldset.disabled = state.vision !== 'mf'; }
      const lensScopeTitle = this.querySelector('[data-rx-lens-scope-title]');
      const lensScopeCopy = this.querySelector('[data-rx-lens-scope-copy]');
      if (lensScopeTitle) lensScopeTitle.textContent = state.vision === 'mf' ? 'Multifocal vision' : 'Single-vision distance';
      if (lensScopeCopy) lensScopeCopy.textContent = state.vision === 'mf'
        ? 'Distance, intermediate and near vision in one pair.'
        : 'For driving, outdoor activities and general distance vision.';
      const singleFrom = this.validCombinations().reduce((minimum, item) => Math.min(minimum, Number(item.servicePrice)), Infinity);
      const multifocalFrom = approvedSunTypeKeys.flatMap((sunTypeKey) => approvedThicknessKeys.map((thicknessKey) => this.opticalService('STD', sunTypeKey, thicknessKey)?.price))
        .filter((price) => Number.isFinite(Number(price))).reduce((minimum, price) => Math.min(minimum, Number(price)), Infinity);
      const setPrice = (selector, value) => this.querySelectorAll(selector).forEach((node) => { node.textContent = Number.isFinite(value) ? `from ${money(value, state.config.product.currency)}` : ''; });
      setPrice('[data-rx-vision-price="sv"]', singleFrom);
      setPrice('[data-rx-vision-price="mf"]', multifocalFrom);
      const standardReference = this.opticalService('STD', 'standard_tint', 'standard_150');
      ['WIDE', 'LARGEST'].forEach((design) => {
        const service = this.opticalService(design, 'standard_tint', 'standard_150');
        const upgrade = standardReference && service ? Math.max(0, Number(service.price) - Number(standardReference.price)) : 0;
        this.querySelectorAll(`[data-rx-mf-design-price="${design}"]`).forEach((node) => { node.textContent = upgrade ? `+${money(upgrade, state.config.product.currency)}` : 'Included'; });
      });
      this.querySelectorAll('[data-rx-add-field]').forEach((field) => {
        const active = state.vision === 'mf'; field.hidden = !active;
        const input = field.querySelector('[data-rx-clinical]'); if (input) input.disabled = !active;
      });
      const fitting = this.querySelector('[data-rx-mf-fitting]'); if (fitting) fitting.hidden = state.vision !== 'mf';
      this.updateTotal();
    }

    renderOptions(focusTarget = null) {
      const state = privateState.get(this);
      const sunTypes = this.validSunTypes(); const thicknesses = this.validThicknesses();
      const tintBox = this.querySelector('[data-rx-tints]'); const packageBox = this.querySelector('[data-rx-packages]');
      const thicknessBox = this.querySelector('[data-rx-thicknesses]');
      [tintBox, packageBox, thicknessBox].forEach((box) => box?.replaceChildren());
      if (!sunTypes.some((item) => item.key === state.sunTypeKey)) state.sunTypeKey = '';
      if (!thicknesses.some((item) => item.key === state.thicknessKey)) state.thicknessKey = '';
      if (!state.sunTypeKey) state.sunTypeKey = sunTypes.find((item) => item.key === 'standard_tint')?.key || '';
      if (!state.thicknessKey) state.thicknessKey = thicknesses.find((item) => item.key === 'standard_150')?.key || '';
      state.packageHandle = state.sunTypeKey; state.finish = state.thicknessKey;
      sunTypes.forEach((item) => {
        this.renderPackageCard(packageBox, item, state.sunTypeKey === item.key, 'sunType')
          .addEventListener('change', () => {
            const hadUpgrade = state.carreraUpgrade;
            this.showSelectionNotice('');
            this.markDirty(); state.sunTypeKey = item.key; state.packageHandle = item.key;
            if (hadUpgrade && !this.carreraUpgradeCompatibility().available) { state.carreraUpgrade = false; this.showSelectionNotice(state.config.labels.carreraUpgradeRemoved); }
            this.track('rx_sun_type_selected', { sun_type_key: item.key, thickness_key: state.thicknessKey, combination_key: this.selectedCombination()?.key || '' });
            this.renderOptions({ container: '[data-rx-packages]', value: item.key });
          });
      });
      thicknesses.forEach((item) => {
        this.renderPackageCard(thicknessBox, item, state.thicknessKey === item.key, 'thickness')
          .addEventListener('change', () => {
            const hadUpgrade = state.carreraUpgrade;
            this.showSelectionNotice('');
            this.markDirty(); state.thicknessKey = item.key; state.finish = item.key;
            if (hadUpgrade && !this.carreraUpgradeCompatibility().available) { state.carreraUpgrade = false; this.showSelectionNotice(state.config.labels.carreraUpgradeRemoved); }
            this.track('rx_thickness_selected', { sun_type_key: state.sunTypeKey, thickness_key: item.key, combination_key: this.selectedCombination()?.key || '' });
            this.renderOptions({ container: '[data-rx-thicknesses]', value: item.key });
          });
      });
      const selectedPackage = this.selectedCombination();
      if (!state.tint && (selectedPackage?.colours || []).includes('Grey')) state.tint = 'Grey';
      const tintChoices = approvedTintChoices.filter((value) => value === otherTintChoice || (selectedPackage?.colours || []).includes(value));
      tintChoices.forEach((value) => {
        this.renderTintCard(tintBox, value, state.tint === value)
          .addEventListener('change', () => {
            const hadUpgrade = state.carreraUpgrade;
            this.showSelectionNotice('');
            this.markDirty(); state.tint = value;
            if (hadUpgrade && !this.carreraUpgradeCompatibility().available) { state.carreraUpgrade = false; this.showSelectionNotice(state.config.labels.carreraUpgradeRemoved); }
            const tintAnalytics = {
              lens_colour: value.toLowerCase(),
              sun_type_key: state.sunTypeKey,
              thickness_key: state.thicknessKey,
              combination_key: selectedPackage?.key || ''
            };
            if (state.config.product.vendor === 'Carrera') {
              tintAnalytics.carrera_eligibility = this.carreraUpgradeCompatibility().available ? 'eligible' : 'ineligible';
            }
            this.track('rx_tint_colour_selected', tintAnalytics);
            this.track('rx_tint_preview_changed', tintAnalytics);
            this.renderOptions({ container: '[data-rx-tints]', value });
          });
      });
      this.renderCarreraUpgrade(); this.updatePreviewImage(); this.updateAssistanceLink();
      const next = this.querySelector('[data-rx-next]'); if (next && state.step === 2) next.disabled = !this.selectedCombination() || !state.tint;
      this.updateTotal();
      if (focusTarget) {
        const container = this.querySelector(focusTarget.container);
        const replacement = [...(container?.querySelectorAll('input[type="radio"]') || [])].find((input) => input.value === focusTarget.value);
        replacement?.focus();
      }
    }

    updateAssistanceLink() {
      const state = privateState.get(this); const variant = this.currentVariant();
      try {
        const url = new URL(state.config.product.assistanceUrl, window.location.origin);
        url.searchParams.set('product', state.config.product.title); url.searchParams.set('variant', variant?.title || '');
        if (state.sunTypeKey) url.searchParams.set('sun_type', state.sunTypeKey);
        if (state.thicknessKey) url.searchParams.set('thickness', state.thicknessKey);
        if (state.tint) url.searchParams.set('tint', state.tint);
        if (this.carreraUpgradeCompatibility().available) url.searchParams.set('carrera_upgrade', state.carreraUpgrade ? 'selected' : 'not_selected');
        if (state.prescriptionMethod) {
          url.searchParams.set('prescription_method', state.prescriptionMethod);
          url.searchParams.set('prescription_status', state.prescriptionMethod === 'email' ? 'awaiting' : 'received');
        }
        if (state.pdMethod) {
          url.searchParams.set('pd_method', state.pdMethod === 'unknown' ? 'provide_later' : state.pdMethod);
          url.searchParams.set('pd_status', state.pdMethod === 'unknown' ? 'awaiting' : 'provided');
        }
        this.querySelectorAll('[data-rx-custom-tint-link], [data-rx-help-cta]').forEach((link) => { link.href = url.toString(); });
      } catch (_) {
        this.querySelectorAll('[data-rx-custom-tint-link], [data-rx-help-cta]').forEach((link) => { link.href = state.config.product.assistanceUrl; });
      }
    }

    renderMethod() {
      const state = privateState.get(this); const method = state.prescriptionMethod;
      const uploadAvailable = state.config.product.uploadReady && (state.uploadProviderReady || Boolean(state.uploadValue));
      const uploadInput = this.querySelector('input[name^="rx-prescription-method"][value="upload"]');
      if (uploadInput) uploadInput.disabled = !uploadAvailable;
      const uploadChoice = this.querySelector('[data-rx-upload-choice]');
      if (uploadChoice) uploadChoice.hidden = !uploadAvailable;
      this.querySelector('[data-rx-upload-panel]').hidden = method !== 'upload';
      this.querySelector('[data-rx-manual-panel]').hidden = method !== 'manual';
      const prismQuestion = this.querySelector('[data-rx-prism-question]'); if (prismQuestion) prismQuestion.hidden = !method;
      const prismFields = this.querySelector('[data-rx-prism-fields]'); if (prismFields) prismFields.hidden = method !== 'manual' || state.prism !== 'yes';
      const email = this.querySelector('[data-rx-email-panel]'); if (email) email.hidden = method !== 'email';
      const emailInstructions = this.querySelector('[data-rx-email-instructions]');
      if (emailInstructions) emailInstructions.textContent = [state.config.product.emailInstructions, state.config.labels.emailLaterHold].filter(Boolean).join(' ');
      this.querySelector('[data-rx-email-choice]').hidden = !state.config.product.emailLaterEnabled;
      const useUpload = this.querySelector('[data-rx-use-upload]'); if (useUpload) useUpload.hidden = !uploadAvailable;
      if (method === 'upload') this.syncUploadState();
    }

    syncUploadState() {
      const state = privateState.get(this); const slot = this.uploadSlot;
      if (!state || !slot || !state.config.product.uploadReady) return;
      const wasComplete = state.uploadComplete;
      state.uploadProviderReady = Boolean(slot.querySelector('.filepond--root, .cl-upload--wrapper, input[type="file"]'));
      const completedItem = slot.querySelector('[data-filepond-item-state="processing-complete"]');
      const uploadItem = slot.querySelector('[data-filepond-item-state]');
      const uploadItemState = String(uploadItem?.getAttribute('data-filepond-item-state') || '');
      state.uploadInProgress = Boolean(uploadItem && !completedItem && /processing|loading|busy/i.test(uploadItemState) && !/error/i.test(uploadItemState));
      state.uploadError = Boolean(uploadItem && /error|failed/i.test(uploadItemState));
      const hasAppValue = [...slot.querySelectorAll('input, textarea')].some((field) => {
        const value = String(field.value || '');
        const isPrescriptionProperty = field.type !== 'file' && this.isUploadReference(value);
        if (isPrescriptionProperty && field.name) {
          field.dataset.rxUploadName = field.name;
          field.removeAttribute('name');
        }
        if (isPrescriptionProperty) state.uploadValue = value;
        return isPrescriptionProperty;
      });
      state.uploadComplete = Boolean(!state.uploadInProgress && !state.uploadError
        && ((completedItem && hasAppValue) || (!uploadItem && this.isUploadReference(state.uploadValue))));
      if (!wasComplete && state.uploadComplete) this.track('rx_upload_completed');
      if (wasComplete && !state.uploadComplete) this.markDirty();
      const uploadInput = this.querySelector('input[name^="rx-prescription-method"][value="upload"]');
      const uploadChoice = this.querySelector('[data-rx-upload-choice]');
      const uploadAvailable = state.config.product.uploadReady && (state.uploadProviderReady || Boolean(state.uploadValue));
      if (uploadInput) uploadInput.disabled = !uploadAvailable;
      if (uploadChoice) uploadChoice.hidden = !uploadAvailable;
      if (!uploadAvailable && state.prescriptionMethod === 'upload') {
        state.prescriptionMethod = ''; if (uploadInput) uploadInput.checked = false;
        this.querySelector('[data-rx-upload-panel]').hidden = true;
      }
      const status = this.querySelector('[data-rx-upload-status]');
      if (status) status.textContent = state.uploadComplete
        ? state.config.labels.uploadComplete
        : state.uploadError
          ? state.config.labels.uploadFailed
          : (state.uploadInProgress || completedItem)
            ? state.config.labels.uploadFinishing
            : state.config.labels.uploadWaiting;
      this.error(''); this.updateForwardState();
    }

    clearUploadState({ preserveRemote = false } = {}) {
      const state = privateState.get(this); const slot = this.uploadSlot;
      if (!state || !slot) return;
      const item = slot.querySelector('.filepond--item');
      const remove = item?.querySelector([
        '.filepond--action-revert-item-processing', '.filepond--action-remove-item',
        '.filepond--action-abort-item-processing', '.filepond--file-action-button'
      ].join(','));
      if (preserveRemote) item?.remove();
      else remove?.click();
      slot.querySelectorAll('input, textarea').forEach((field) => {
        const name = String(field.dataset.rxUploadName || field.name || '');
        if (!/prescription upload/i.test(name)) return;
        field.removeAttribute('name'); field.value = '';
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      state.uploadComplete = false;
      state.uploadValue = '';
      state.uploadInProgress = false; state.uploadError = false;
      const status = this.querySelector('[data-rx-upload-status]');
      if (status) status.textContent = state.config.labels.uploadWaiting;
      this.updateForwardState();
    }

    renderPd() {
      const state = privateState.get(this); const method = state.pdMethod;
      const singleInput = this.querySelector('input[name^="rx-pd-method"][value="single"]');
      if (singleInput) singleInput.disabled = state.vision === 'mf';
      if (state.vision === 'mf' && state.pdMethod === 'single') {
        state.pdMethod = 'dual';
        const dualInput = this.querySelector('input[name^="rx-pd-method"][value="dual"]'); if (dualInput) dualInput.checked = true;
      }
      this.querySelector('[data-rx-pd-values]').hidden = !['single', 'dual'].includes(method);
      this.querySelector('[data-rx-pd-single]').hidden = method !== 'single';
      this.querySelector('[data-rx-pd-dual]').hidden = method !== 'dual';
      const pdHelp = this.querySelector('[data-rx-pd-help-panel]'); pdHelp.hidden = method !== 'help';
      const manualSection = this.querySelector('[data-rx-pd-manual-section]');
      const manualOpen = this.querySelector('[data-rx-pd-manual-open]');
      const manualVisible = method === 'help' && state.pdManualExpanded;
      if (manualSection) manualSection.hidden = !manualVisible;
      if (manualOpen) {
        manualOpen.setAttribute('aria-expanded', String(manualVisible));
        manualOpen.textContent = manualVisible ? manualOpen.dataset.closeLabel : manualOpen.dataset.openLabel;
      }
      const pdLaterInput = this.querySelector('input[name^="rx-pd-method"][value="unknown"]');
      if (pdLaterInput) pdLaterInput.checked = method === 'unknown';
      const source = this.querySelector('[data-rx-pd-source]'); if (source) source.hidden = state.pdSource !== 'camera' || !['single', 'dual'].includes(method);
    }

    selectPdMethod(method) {
      if (!['single', 'dual'].includes(method)) return;
      const state = privateState.get(this);
      state.pdSource = '';
      state.pdProvenance = null;
      const input = this.querySelector(`input[name^="rx-pd-method"][value="${method}"]`);
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      requestAnimationFrame(() => this.querySelector(method === 'single' ? '[data-rx-clinical="pd"]' : '[data-rx-clinical="pdRight"]')?.focus());
    }

    selectPdLater() {
      const state = privateState.get(this);
      if (!state.config.product.pdFollowupEnabled) return false;
      const hasEnteredPd = ['pd', 'pdRight', 'pdLeft'].some((key) => String(state.clinical[key] || '').trim() !== '');
      if (hasEnteredPd && !window.confirm(state.config.labels.pdLaterDiscardConfirm)) return false;
      ['pd', 'pdRight', 'pdLeft'].forEach((key) => {
        delete state.clinical[key];
        const field = this.querySelector(`[data-rx-clinical="${key}"]`);
        if (field) field.value = '';
      });
      this.querySelectorAll('input[name^="rx-pd-method"]').forEach((input) => { input.checked = input.value === 'unknown'; });
      state.pdMethod = 'unknown';
      state.pdSource = '';
      state.pdProvenance = null;
      state.pdManualExpanded = false;
      this.pdController?.clearValidationData?.(false);
      this.clearFieldErrors();
      this.markDirty();
      this.track('rx_pd_later_selected', { pd_method: 'provide_later', pd_status: 'awaiting' });
      this.renderPd();
      this.error('');
      this.updateForwardState();
      requestAnimationFrame(() => this.querySelector('input[name^="rx-pd-method"][value="unknown"]')?.focus());
      return true;
    }

    loadPdAsset(tag, attributes, key) {
      window.CityOpticsPdAssetPromises = window.CityOpticsPdAssetPromises || {};
      if (window.CityOpticsPdAssetPromises[key]) return window.CityOpticsPdAssetPromises[key];
      window.CityOpticsPdAssetPromises[key] = new Promise((resolve, reject) => {
        const existing = document.querySelector(`[data-rx-pd-asset="${CSS.escape(key)}"]`);
        if (existing?.dataset.loaded === 'true') { resolve(); return; }
        const asset = existing || document.createElement(tag);
        Object.entries(attributes).forEach(([name, value]) => asset.setAttribute(name, value));
        asset.dataset.rxPdAsset = key;
        asset.addEventListener('load', () => { asset.dataset.loaded = 'true'; resolve(); }, { once: true });
        asset.addEventListener('error', (error) => {
          asset.remove();
          delete window.CityOpticsPdAssetPromises[key];
          reject(error);
        }, { once: true });
        if (!existing) document.head.append(asset);
      });
      return window.CityOpticsPdAssetPromises[key];
    }

    async openPdCamera(opener) {
      const state = privateState.get(this);
      const directTest = directPdTestRequested();
      if (!state.config.product.cameraPdEnabled && !(directTest && state.config.product.cameraPdDirectTestAvailable)) return;
      const root = this.querySelector('[data-rx-pd-tool]');
      if (!root) return;
      const stylesheetUrl = directTest
        ? withPdTestBuild(state.config.product.cameraPdStylesheetUrl)
        : state.config.product.cameraPdStylesheetUrl;
      const scriptUrl = directTest
        ? withPdTestBuild(state.config.product.cameraPdScriptUrl)
        : state.config.product.cameraPdScriptUrl;
      const currentPdBuild = window.CityOpticsPdMeasurement?.build;
      const loadScript = !window.CityOpticsPdMeasurement || (directTest && currentPdBuild !== PD_TEST_BUILD);
      this.pdOpener = opener;
      const openToken = (this.pdOpenToken || 0) + 1;
      this.pdOpenToken = openToken;
      try {
        await Promise.all([
          this.loadPdAsset('link', { rel: 'stylesheet', href: stylesheetUrl }, directTest ? `styles-${PD_TEST_BUILD}` : 'styles'),
          loadScript
            ? this.loadPdAsset('script', { src: scriptUrl }, directTest ? `script-${PD_TEST_BUILD}` : 'script')
            : Promise.resolve()
        ]);
        if (this.pdOpenToken !== openToken || !this.isConnected || !this.dialog.open) return;
        if (!window.CityOpticsPdMeasurement?.create) throw new Error('unavailable');
        if (!this.pdController) {
          this.pdController = window.CityOpticsPdMeasurement.create(root, {
            labels: state.config.labels.cameraPd,
            runtimeUrl: state.config.product.cameraPdRuntimeUrl,
            wasmUrl: state.config.product.cameraPdWasmUrl,
            modelUrl: state.config.product.cameraPdModelUrl,
            validationMode: Boolean(state.config.product.cameraPdValidationMode),
            debugMode: Boolean(state.config.product.cameraPdDebugMode),
            manualCornersEnabled: Boolean(state.config.product.cameraPdManualCornersEnabled),
            manualCornersDelayMs: Number(state.config.product.cameraPdManualCornersDelay || 7) * 1000,
            modelLoadTimeoutMs: Number(state.config.product.cameraPdModelLoadTimeout || 20) * 1000,
            limits: {
              singleMin: state.config.product.pdSingleMin,
              singleMax: state.config.product.pdSingleMax,
              dualMin: state.config.product.pdDualMin,
              dualMax: state.config.product.pdDualMax
            }
          });
        }
        this.setPdToolModalState(true);
        this.pdController.open();
      } catch (_) {
        this.setPdToolModalState(false);
        this.track('measurement_failed');
        this.error(state.config.labels.cameraPd?.modelFailed || state.config.labels.pdHelpRequired);
        this.showPdManualInstructions();
      }
    }

    setPdToolModalState(active) {
      const root = this.querySelector('[data-rx-pd-tool]');
      if (!root) return;
      if (active) {
        const siblings = [...(root.parentElement?.children || [])].filter((element) => element !== root);
        const chrome = [this.dialog.querySelector(':scope > .rx-dialog__header'), this.dialog.querySelector(':scope > .rx-dialog__footer')].filter(Boolean);
        this.pdModalIsolation = [...new Set([...siblings, ...chrome])].map((element) => ({
          element,
          inert: element.hasAttribute('inert'),
          ariaHidden: element.getAttribute('aria-hidden')
        }));
        this.pdModalIsolation.forEach(({ element }) => {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        });
        root.setAttribute('aria-hidden', 'false');
        return;
      }
      (this.pdModalIsolation || []).forEach(({ element, inert, ariaHidden }) => {
        if (!inert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.pdModalIsolation = [];
      root.setAttribute('aria-hidden', 'true');
    }

    applyPdMeasurement(measurement) {
      const state = privateState.get(this);
      const mapped = window.CityOpticsPdMeasurement?.normaliseCameraPdMeasurement?.(measurement);
      if (!mapped) return;
      state.pdMethod = mapped.pdMethod;
      state.pdSource = mapped.pdSource;
      state.pdProvenance = mapped.provenance || {
        build: measurement.build || PD_TEST_BUILD,
        calibrationMethod: measurement.calibrationMethod || 'not_available',
        reviewStatus: measurement.reviewStatus || 'validation_required'
      };
      delete state.clinical.pd;
      delete state.clinical.pdRight;
      delete state.clinical.pdLeft;
      Object.assign(state.clinical, mapped.clinical);
      this.querySelectorAll('input[name^="rx-pd-method"]').forEach((input) => { input.checked = input.value === state.pdMethod; });
      [['pd', state.clinical.pd], ['pdRight', state.clinical.pdRight], ['pdLeft', state.clinical.pdLeft]].forEach(([key, value]) => {
        const input = this.querySelector(`[data-rx-clinical="${key}"]`);
        if (input) input.value = value || '';
      });
      this.clearFieldErrors();
      this.markDirty();
      this.error('');
      this.renderPd();
      this.updateForwardState();
      requestAnimationFrame(() => this.querySelector('[data-rx-pd-source]')?.focus());
    }

    showPdManualInstructions() {
      const state = privateState.get(this);
      state.pdMethod = 'help';
      state.pdSource = '';
      state.pdProvenance = null;
      state.pdManualExpanded = true;
      const input = this.querySelector('input[name^="rx-pd-method"][value="help"]');
      if (input) input.checked = true;
      this.renderPd();
      requestAnimationFrame(() => this.querySelector('[data-rx-pd-manual-heading]')?.focus());
    }

    selectedPackage() { return this.selectedCombination(); }
    updateTotal() {
      const state = privateState.get(this); const total = money(this.totalAmount(), state.config.product.currency);
      this.querySelectorAll('[data-rx-total]').forEach((node) => { node.textContent = `${state.config.labels.footerTotal || state.config.labels.total}: ${total}`; });
      this.querySelectorAll('[data-rx-header-total]').forEach((node) => { node.textContent = `${state.config.labels.currentTotal || 'Current total'}: ${total}`; });
      this.renderPurchaseState();
      this.syncPurchaseViews();
    }

    renderPurchaseState() {
      const state = privateState.get(this); const labels = state.config.labels;
      const purchaseArea = this.closest('.product__info-container') || document;
      const draft = purchaseArea.querySelector('[data-rx-draft]');
      if (!draft) return;
      draft.hidden = !state.builderOpened;
      const summary = draft.querySelector('[data-rx-draft-summary]');
      if (!summary || !state.builderOpened) return;
      const type = this.selectedSunType()?.title || labels.singleVisionDistance || 'Prescription lenses';
      const colour = state.tint || labels.tint || 'Colour not selected';
      summary.textContent = (labels.draftSummary || '[type] · [colour] · Step [step] of 5')
        .replace('[type]', type).replace('[colour]', colour).replace('[step]', String(state.step));
    }

    prescriptionStartingLabel(total) {
      const state = privateState.get(this);
      return (state.config.labels.buildPrescriptionSunglasses || 'Build prescription sunglasses — from [price]')
        .replace('[price]', money(total, state.config.product.currency));
    }

    prescriptionSectionCtaLabel(total) {
      const state = privateState.get(this);
      const currency = String(state.config.product.currency || '').trim().toUpperCase();
      return [this.prescriptionStartingLabel(total), currency, 'total'].filter(Boolean).join(' ');
    }

    syncPurchaseViews() {
      this.placePriceMatch();
      const state = privateState.get(this); const selectedVariant = this.currentVariant(); const combinations = this.validCombinations();
      const lowestPrice = combinations.length ? Math.min(...combinations.map((item) => Number(item.servicePrice))) : 0;
      const prescriptionVariant = this.prescriptionVariant();
      const prescriptionStartingTotal = Number(prescriptionVariant?.price || 0) + lowestPrice;
      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      const picker = this.closest('.product__info-container')?.querySelector('[data-city-custom-polarised-addon-price]');
      const customPolarisedPrice = this.form?.dataset.cityCustomPolarisedSelected === 'true'
        ? Number(picker?.dataset.cityCustomPolarisedAddonPrice || 0)
        : 0;
      const nonPrescriptionTotal = (Number(selectedVariant?.price || 0) + customPolarisedPrice) * quantity;
      const nonPrescriptionLabel = selectedVariant?.available
        ? (state.config.labels.addSunglassesAsShown || 'Add sunglasses as shown — [price]').replace('[price]', money(nonPrescriptionTotal, state.config.product.currency))
        : state.config.labels.soldOut;
      const nonPrescriptionAccessible = selectedVariant?.available
        ? (state.config.labels.addSunglassesAsShownAccessible || 'Add sunglasses as shown to cart, total [price]').replace('[price]', money(nonPrescriptionTotal, state.config.product.currency))
        : state.config.labels.soldOut;
      const mainButton = this.mainButton || this.form?.querySelector('[name="add"]');
      if (mainButton) {
        const labelNode = mainButton.querySelector('span:not(.loading__spinner)');
        if (labelNode && labelNode.textContent.trim() !== nonPrescriptionLabel) labelNode.textContent = nonPrescriptionLabel;
        else if (!labelNode && mainButton.textContent.trim() !== nonPrescriptionLabel) mainButton.textContent = nonPrescriptionLabel;
        mainButton.setAttribute('aria-label', nonPrescriptionAccessible);
      }
      document.querySelectorAll('[data-city-sticky-submit]').forEach((button) => {
        this.setStickyBuyLabel(button, nonPrescriptionLabel, nonPrescriptionTotal, Boolean(selectedVariant?.available));
        button.setAttribute('aria-label', nonPrescriptionAccessible);
        button.disabled = Boolean(mainButton?.disabled || !selectedVariant?.available);
        button.setAttribute('aria-disabled', String(button.disabled));
      });
      document.querySelectorAll('[data-rx-entry-button]').forEach((button) => {
        button.hidden = !state.eligible;
        button.disabled = !state.eligible;
        button.setAttribute('aria-disabled', String(!state.eligible));
        if (state.eligible) {
          if (state.builderOpened) {
            button.textContent = state.config.labels.continuePrescription || 'Continue building prescription sunglasses';
            button.setAttribute('aria-label', button.textContent);
          } else {
            const startingLabel = this.prescriptionStartingLabel(prescriptionStartingTotal);
            const startingAccessible = (state.config.labels.buildPrescriptionSunglassesAccessible || 'Build prescription sunglasses, starting total [price]').replace('[price]', money(prescriptionStartingTotal, state.config.product.currency));
            button.textContent = startingLabel;
            button.setAttribute('aria-label', startingAccessible);
          }
        }
      });
      document.querySelectorAll('[data-rx-sticky-prescription]').forEach((button) => {
        button.hidden = !state.eligible;
        button.disabled = !state.eligible;
        button.setAttribute('aria-disabled', String(button.disabled));
        if (!state.eligible) return;
        const label = state.builderOpened
          ? state.config.labels.continuePrescription || 'Continue building prescription sunglasses'
          : this.prescriptionStartingLabel(prescriptionStartingTotal);
        this.setStickyPrescriptionLabel(button, label, prescriptionStartingTotal);
        button.setAttribute('aria-label', label);
      });
      document.querySelectorAll('[data-rx-section-cta]').forEach((button) => {
        button.hidden = !state.eligible;
        button.disabled = !state.eligible;
        button.setAttribute('aria-disabled', String(button.disabled));
        if (!state.eligible) return;
        button.textContent = this.prescriptionSectionCtaLabel(prescriptionStartingTotal);
        button.setAttribute('aria-label', button.textContent);
      });
    }

    validate(step = privateState.get(this).step, options = {}) {
      const state = privateState.get(this); const c = state.clinical;
      const isNumeric = (value) => String(value || '').trim() !== '' && Number.isFinite(Number(value));
      const isSphere = (value) => isNumeric(value) || String(value || '').trim().toLowerCase() === 'plano';
      const issue = (message, selector, errorCode = '') => ({ message, selector, errorCode });
      if (step === 1) {
        if (!state.vision) return issue('Choose Single Vision or Multifocal.', '[data-rx-vision]');
        if (state.vision === 'mf' && !state.multifocalDesign) return issue('Choose a multifocal design.', '[data-rx-mf-design-input]');
      }
      if (step === 2) {
        const lens = this.selectedPackage();
        if (!this.selectedSunType()) return issue(state.config.labels.choose, '[data-rx-packages] input');
        if (!this.selectedThickness()) return issue(state.config.labels.choose, '[data-rx-thicknesses] input');
        if (!lens) return issue(state.config.labels.choose, '[data-rx-thicknesses] input');
        const directTintValid = approvedTintColours.includes(state.tint) && (lens.colours || []).includes(state.tint);
        const otherTintValid = state.tint === otherTintChoice;
        if (!directTintValid && !otherTintValid) return issue(state.config.labels.choose, '[data-rx-tints] input');
        if (state.carreraUpgrade && !this.carreraUpgradeCompatibility().available) return issue(state.config.labels.carreraUpgradeUnavailable, '[data-rx-carrera-upgrade-input]');
      }
      if (step === 3) {
        if (!state.prescriptionMethod) return issue(state.config.labels.choose, 'input[name^="rx-prescription-method"]:not(:disabled)');
        if (state.prescriptionMethod === 'upload' && state.uploadError) return issue(state.config.labels.uploadFailed, '[data-rx-upload-status]', 'upload_failed');
        if (state.prescriptionMethod === 'upload' && (!state.config.product.uploadReady || !state.uploadComplete || state.uploadInProgress)) return issue(state.config.labels.uploadRequired, '[data-rx-upload-slot]', 'upload_required');
        if (state.prescriptionMethod === 'manual') {
          if (!isSphere(c.odSph)) return issue(state.config.labels.rightSphere || state.config.labels.required, '[data-rx-clinical="odSph"]', 'right_sphere_required');
          if (String(c.odCyl || '').trim() && !isNumeric(c.odCyl)) return issue(state.config.labels.invalidNumber || state.config.labels.required, '[data-rx-clinical="odCyl"]');
          if (String(c.odCyl || '').trim() && Number(c.odCyl) !== 0 && !isNumeric(c.odAxis)) return issue(state.config.labels.rightAxis || state.config.labels.required, '[data-rx-clinical="odAxis"]', 'right_axis_required');
          if (!isSphere(c.osSph)) return issue(state.config.labels.leftSphere || state.config.labels.required, '[data-rx-clinical="osSph"]', 'left_sphere_required');
          if (String(c.osCyl || '').trim() && !isNumeric(c.osCyl)) return issue(state.config.labels.invalidNumber || state.config.labels.required, '[data-rx-clinical="osCyl"]');
          if (String(c.osCyl || '').trim() && Number(c.osCyl) !== 0 && !isNumeric(c.osAxis)) return issue(state.config.labels.leftAxis || state.config.labels.required, '[data-rx-clinical="osAxis"]', 'left_axis_required');
          const validAdd = (value) => isNumeric(value) && Number(value) >= 0.25 && Number(value) <= 5 && Math.abs((Number(value) * 4) - Math.round(Number(value) * 4)) < 0.001;
          if (state.vision === 'mf' && !validAdd(c.odAdd)) return issue('Enter right-eye ADD from +0.25 to +5.00 in 0.25 steps.', '[data-rx-clinical="odAdd"]', 'right_add_required');
          if (state.vision === 'mf' && !validAdd(c.osAdd)) return issue('Enter left-eye ADD from +0.25 to +5.00 in 0.25 steps.', '[data-rx-clinical="osAdd"]', 'left_add_required');
        }
        const prismIssue = this.prismValidationIssue();
        if (prismIssue) return issue(prismIssue.message, prismIssue.selector, prismIssue.errorCode);
      }
      if (step === 4) {
        if (!state.pdMethod) return issue(state.config.labels.choose, 'input[name^="rx-pd-method"]');
        if (state.pdMethod === 'help') return issue(state.config.labels.pdHelpRequired, '[data-rx-pd-help-select]', 'pd_required');
        if (state.pdMethod === 'unknown' && !state.config.product.pdFollowupEnabled) return issue(state.config.labels.choose, 'input[name^="rx-pd-method"]:not(:disabled)');
        const singleMin = Number(state.config.product.pdSingleMin); const singleMax = Number(state.config.product.pdSingleMax);
        const dualMin = Number(state.config.product.pdDualMin); const dualMax = Number(state.config.product.pdDualMax);
        if (state.pdMethod === 'single' && String(c.pd || '').trim() === '') return issue(state.config.labels.required, '[data-rx-clinical="pd"]', 'pd_required');
        if (state.vision === 'mf' && state.pdMethod === 'single') return issue('Multifocal lenses need separate right and left PD values.', 'input[name^="rx-pd-method"][value="dual"]', 'pd_required');
        if (state.pdMethod === 'single' && !isNumeric(c.pd)) return issue(state.config.labels.pdSingleInvalid || state.config.labels.invalidNumber, '[data-rx-clinical="pd"]');
        if (state.pdMethod === 'single' && (Number(c.pd) < singleMin || Number(c.pd) > singleMax)) return issue((state.config.labels.pdSingleRange || state.config.labels.invalidNumber).replace('[min]', singleMin).replace('[max]', singleMax), '[data-rx-clinical="pd"]');
        if (state.pdMethod === 'dual' && String(c.pdRight || '').trim() === '') return issue(state.config.labels.required, '[data-rx-clinical="pdRight"]', 'right_pd_required');
        if (state.pdMethod === 'dual' && !isNumeric(c.pdRight)) return issue(state.config.labels.pdDualInvalid || state.config.labels.invalidNumber, '[data-rx-clinical="pdRight"]');
        if (state.pdMethod === 'dual' && (Number(c.pdRight) < dualMin || Number(c.pdRight) > dualMax)) return issue((state.config.labels.pdDualRange || state.config.labels.invalidNumber).replace('[min]', dualMin).replace('[max]', dualMax), '[data-rx-clinical="pdRight"]');
        if (state.pdMethod === 'dual' && String(c.pdLeft || '').trim() === '') return issue(state.config.labels.required, '[data-rx-clinical="pdLeft"]', 'left_pd_required');
        if (state.pdMethod === 'dual' && !isNumeric(c.pdLeft)) return issue(state.config.labels.pdDualInvalid || state.config.labels.invalidNumber, '[data-rx-clinical="pdLeft"]');
        if (state.pdMethod === 'dual' && (Number(c.pdLeft) < dualMin || Number(c.pdLeft) > dualMax)) return issue((state.config.labels.pdDualRange || state.config.labels.invalidNumber).replace('[min]', dualMin).replace('[max]', dualMax), '[data-rx-clinical="pdLeft"]');
        if (state.pdSource === 'camera' && !options.allowCameraReview) return issue(state.config.labels.cameraPdCheckoutBlocked, '[data-rx-pd-source]', 'camera_pd_validation_required');
      }
      if (step === 5 && state.vision === 'mf' && !state.multifocalAcknowledged) return issue('Confirm that you’ll email the multifocal fitting photo after checkout.', '[data-rx-mf-acknowledgement]', 'multifocal_photo_acknowledgement_required');
      return null;
    }

    move(direction) {
      const state = privateState.get(this);
      if (direction > 0) { const issue = this.validate(state.step, { allowCameraReview: state.step === 4 }); if (issue) { this.presentValidationIssue(issue); return; } }
      state.step = Math.min(5, Math.max(1, state.step + direction)); this.error(''); this.render(true);
    }

    completeConfiguration() {
      const state = privateState.get(this);
      for (let step = 1; step <= 5; step += 1) {
        const issue = this.validate(step);
        if (!issue) continue;
        state.step = step; state.completed = false; state.needsReview = true;
        this.render(true); this.presentValidationIssue(issue); return;
      }
      if (state.config.product.suitabilityReviewRequired && !state.suitabilityAccepted) {
        const acknowledgement = this.querySelector('[data-rx-suitability-acknowledgement]');
        const suitabilityError = this.querySelector('[data-rx-suitability-error]');
        acknowledgement?.setAttribute('aria-invalid', 'true');
        if (suitabilityError) suitabilityError.hidden = false;
        this.error(state.config.labels.suitabilityReviewRequired);
        requestAnimationFrame(() => acknowledgement?.focus());
        return;
      }
      if (state.config.product.cartEnabled) return this.addPrescriptionToCart();
      state.completed = true; state.needsReview = false;
      this.track('rx_builder_completed');
      this.error(''); this.close(); this.renderPurchaseState(); this.syncPurchaseViews();
    }

    clearFieldError(key) {
      const message = this.querySelector(`[data-rx-field-error="${CSS.escape(key || '')}"]`);
      const input = this.querySelector(`[data-rx-clinical="${CSS.escape(key || '')}"]`);
      if (message) { message.textContent = ''; message.hidden = true; }
      input?.removeAttribute('aria-invalid');
    }

    validatePdField(input) {
      const state = privateState.get(this);
      const key = input?.dataset?.rxClinical;
      const methodMatches = (state.pdMethod === 'single' && key === 'pd')
        || (state.pdMethod === 'dual' && ['pdRight', 'pdLeft'].includes(key));
      if (!input || !methodMatches) { if (key) this.clearFieldError(key); return true; }
      const value = String(input.value || '').trim();
      const numeric = value !== '' && Number.isFinite(Number(value));
      const min = Number(key === 'pd' ? state.config.product.pdSingleMin : state.config.product.pdDualMin);
      const max = Number(key === 'pd' ? state.config.product.pdSingleMax : state.config.product.pdDualMax);
      let message = '';
      if (!value) message = state.config.labels.required;
      else if (!numeric) message = key === 'pd' ? state.config.labels.pdSingleInvalid : state.config.labels.pdDualInvalid;
      else if (Number(value) < min || Number(value) > max) {
        const rangeLabel = key === 'pd' ? state.config.labels.pdSingleRange : state.config.labels.pdDualRange;
        message = rangeLabel.replace('[min]', min).replace('[max]', max);
      }
      const error = this.querySelector(`[data-rx-field-error="${CSS.escape(key)}"]`);
      const wasInvalid = input.getAttribute('aria-invalid') === 'true';
      if (!message) { this.clearFieldError(key); return true; }
      if (error) { error.textContent = message; error.hidden = false; }
      input.setAttribute('aria-invalid', 'true');
      if (!wasInvalid) this.track('rx_pd_validation_error', { pd_method: state.pdMethod, pd_status: 'incomplete' });
      return false;
    }

    clearFieldErrors() { this.querySelectorAll('[data-rx-field-error]').forEach((node) => this.clearFieldError(node.dataset.rxFieldError)); }

    presentValidationIssue(issue) {
      this.clearFieldErrors();
      const target = this.querySelector(issue.selector);
      const key = target?.dataset?.rxClinical;
      const fieldMessage = key ? this.querySelector(`[data-rx-field-error="${CSS.escape(key)}"]`) : null;
      if (fieldMessage) { fieldMessage.textContent = issue.message; fieldMessage.hidden = false; target.setAttribute('aria-invalid', 'true'); }
      this.error(issue.message);
      this.track('rx_validation_error', { error_code: issue.errorCode });
      if (privateState.get(this).step === 4) {
        const pdMethod = privateState.get(this).pdMethod || 'none';
        this.track('rx_pd_validation_error', { pd_method: pdMethod === 'unknown' ? 'provide_later' : pdMethod, pd_status: 'incomplete' });
      }
      const focusTarget = target?.matches?.('[data-rx-upload-slot]')
        ? target.querySelector('input[type="file"], button, [tabindex]') || target
        : target;
      if (focusTarget && !focusTarget.hasAttribute('tabindex') && !/^(INPUT|BUTTON|A|SELECT|TEXTAREA)$/.test(focusTarget.tagName)) focusTarget.tabIndex = -1;
      requestAnimationFrame(() => focusTarget?.focus?.());
    }

    updateForwardState() {
      const state = privateState.get(this); const next = this.querySelector('[data-rx-next]');
      if (!state || !next) return;
      const validationIssue = this.validate(state.step, { allowCameraReview: state.step === 4 });
      const suitabilityIssue = state.step === 5 && state.config.product.suitabilityReviewRequired && !state.suitabilityAccepted
        ? state.config.labels.suitabilityReviewRequired
        : '';
      next.disabled = Boolean(validationIssue || suitabilityIssue) || state.addingToCart;
      next.setAttribute('aria-disabled', String(next.disabled));
      next.setAttribute('aria-busy', String(state.addingToCart));
      const hint = this.querySelector('[data-rx-forward-hint]');
      if (hint) hint.textContent = validationIssue?.message || suitabilityIssue;
      const ids = unique([this.querySelector('[data-rx-forward-hint]')?.id, next.getAttribute('aria-describedby')?.split(/\s+/).find((id) => /FinalCtaNote/.test(id))]);
      if (ids.length) next.setAttribute('aria-describedby', ids.join(' '));
    }

    error(message) { const box = this.querySelector('[data-rx-error]'); box.textContent = message; box.hidden = !message; if (message) box.focus(); }
    selectedControlLabel(prefix, value) {
      const input = this.querySelector(`input[name^="${prefix}"][value="${CSS.escape(value || '')}"]`);
      return input?.closest('label')?.querySelector('strong')?.textContent?.trim() || value;
    }
    renderReview() {
      const state = privateState.get(this); const variant = this.prescriptionVariant(); const lens = this.selectedPackage(); const labels = state.config.labels;
      const sunType = this.selectedSunType(); const thickness = this.selectedThickness();
      const prescriptionStatus = state.prescriptionMethod === 'email' ? labels.prescriptionAwaiting : labels.prescriptionReceived;
      const c = state.clinical;
      const pdSource = state.pdSource === 'camera' ? ` · ${labels.cameraPdMeasured}` : '';
      const pdStatus = state.pdMethod === 'unknown'
        ? labels.pdAwaiting
        : state.pdMethod === 'single' && c.pd
          ? `${labels.pdProvided} — ${c.pd} mm${pdSource}`
          : state.pdMethod === 'dual' && c.pdRight && c.pdLeft
            ? `${labels.pdProvided} — Right ${c.pdRight} mm / Left ${c.pdLeft} mm${pdSource}`
            : labels.pdProvided;
      const upgradeOffered = this.carreraUpgradeCompatibility().available || state.carreraUpgrade;
      const prescriptionMethod = this.selectedControlLabel('rx-prescription-method', state.prescriptionMethod);
      const pdMethod = state.pdMethod === 'unknown' ? labels.pdProvideLater : this.selectedControlLabel('rx-pd-method', state.pdMethod);
      const lensRows = [
        ['Vision', this.visionLabel()],
        ['Multifocal design', state.vision === 'mf' ? this.multifocalDesignLabel() : ''],
        [labels.lens, this.packageTitle()],
        [labels.features || 'Package features', (lens?.features || []).join(', ')],
        [labels.tint || 'Lens colour', state.tint],
        [labels.carreraUpgradeLabel || 'Genuine Carrera upgrade', upgradeOffered ? (state.carreraUpgrade ? labels.carreraUpgradeAdded : labels.carreraUpgradeNotAdded) : ''],
        [labels.leadTime, lens?.leadTime || '']
      ];
      const measurementRows = [
        [labels.method, `${prescriptionMethod} · ${prescriptionStatus}`],
        ['Prism', state.prism === 'yes' ? 'Yes — prism treatment added' : 'No prism'],
        [labels.pdMethod, pdMethod],
        [labels.pdStatus, pdStatus]
      ];
      const appendRows = (selector, rows) => {
        const dl = this.querySelector(selector); dl?.replaceChildren();
        rows.filter(([, value]) => String(value || '').trim()).forEach(([term, value]) => {
          const dt = document.createElement('dt'); const dd = document.createElement('dd');
          dt.textContent = term; dd.textContent = escapeText(value); dl?.append(dt, dd);
        });
      };
      appendRows('[data-rx-review-lenses]', lensRows);
      appendRows('[data-rx-review-measurements]', measurementRows);
      const pricingRows = [
        [labels.framePrice || labels.frameSubtotal, money(variant?.price || 0, state.config.product.currency)],
        [sunType?.title || 'Lens type', money(sunType?.basePrice || 0, state.config.product.currency)],
        ['Multifocal upgrade', state.vision === 'mf' ? money(this.multifocalBaseUpgrade(), state.config.product.currency) : ''],
        [this.multifocalDesignLabel(), state.vision === 'mf' && this.multifocalDesignUpgrade() > 0 ? money(this.multifocalDesignUpgrade(), state.config.product.currency) : ''],
        [`${thickness?.title || 'Lens thickness'} ${thickness?.technical || ''}`.trim(), Number(thickness?.surcharge || 0) ? money(thickness.surcharge, state.config.product.currency) : 'Included'],
        [labels.upgradePrice || labels.carreraUpgradeSubtotal, state.carreraUpgrade ? money(this.upgradePrice(), state.config.product.currency) : ''],
        ['Prism treatment', state.prism === 'yes' ? money(this.prismPrice(), state.config.product.currency) : ''],
        [labels.total, money(this.totalAmount(), state.config.product.currency)]
      ];
      appendRows('[data-rx-review-pricing]', pricingRows);
      const setReviewImage = (selector, source) => {
        const image = this.querySelector(selector); if (!image) return;
        image.hidden = !source; if (source) image.src = source;
      };
      const packageIcon = state.config.product.sunTypeIcons?.[state.sunTypeKey] || '';
      setReviewImage('[data-rx-review-lens-icon]', packageIcon);
      setReviewImage('[data-rx-review-method-icon]', state.config.product.builderIcons?.[state.prescriptionMethod] || '');
      const awaitingPrescription = state.prescriptionMethod === 'email'; const awaitingPd = state.pdMethod === 'unknown';
      const suitabilityAcknowledgement = this.querySelector('[data-rx-suitability-acknowledgement]');
      if (suitabilityAcknowledgement) suitabilityAcknowledgement.checked = state.suitabilityAccepted;
      const status = this.querySelector('[data-rx-review-status]'); const statusHeading = this.querySelector('[data-rx-review-status-heading]'); const statusCopy = this.querySelector('[data-rx-review-status-copy]');
      if (status) status.hidden = !awaitingPrescription && !awaitingPd;
      if (statusHeading && statusCopy) {
        if (awaitingPrescription && awaitingPd) {
          statusHeading.textContent = labels.reviewInformationAwaitingHeading; statusCopy.textContent = labels.reviewInformationAwaitingCopy;
        } else if (awaitingPrescription) {
          statusHeading.textContent = labels.reviewPrescriptionAwaitingHeading; statusCopy.textContent = labels.reviewPrescriptionAwaitingCopy;
        } else if (awaitingPd) {
          statusHeading.textContent = labels.reviewPdAwaitingHeading; statusCopy.textContent = labels.reviewPdAwaitingCopy;
        } else { statusHeading.textContent = ''; statusCopy.textContent = ''; }
      }
    }

    render(focusStep = false) {
      const state = privateState.get(this); const labels = state.config.labels;
      this.dialog.dataset.rxReviewStep = String(state.step === 5);
      this.querySelectorAll('[data-rx-step]').forEach((section) => { section.hidden = Number(section.dataset.rxStep) !== state.step; });
      this.updateDialogHeader();
      this.querySelector('[data-rx-back]').hidden = state.step === 1;
      const finalCta = state.config.product.cartEnabled
        ? (state.addingToCart
          ? (state.editingGroupId ? labels.updatingCart : labels.addingToCart)
          : ((state.editingGroupId ? labels.updateCart : labels.addPrescriptionSunglassesToCart)
            || (state.editingGroupId ? 'Update cart — [price]' : 'Add prescription sunglasses to cart — [price]'))
            .replace('[price]', money(this.totalAmount(), state.config.product.currency)))
        : (labels.saveChoices || 'Save prescription choices');
      const nextLabels = ['Continue to lenses and colour', labels.continueToPrescription, labels.continueToPd, labels.reviewYourChoices, finalCta];
      const next = this.querySelector('[data-rx-next]'); next.textContent = nextLabels[state.step - 1] || labels.continue; next.setAttribute('aria-label', next.textContent); next.hidden = false;
      const helpCta = this.querySelector('[data-rx-help-cta]'); if (helpCta) helpCta.hidden = state.step !== 5 || state.config.product.cartEnabled;
      this.renderOptions(); this.renderVision(); this.renderMethod(); this.renderPd(); if (state.step === 5) this.renderReview();
      requestAnimationFrame(() => {
        const footer = this.querySelector('.rx-dialog__footer');
        if (footer) this.dialog.style.setProperty('--rx-footer-height', `${footer.offsetHeight}px`);
      });
      this.renderPurchaseState();
      this.updateForwardState();
      if (focusStep && this.dialog.open) requestAnimationFrame(() => this.focusCurrentStep());
    }

    disconnectedCallback() {
      this.pdOpenToken = (this.pdOpenToken || 0) + 1;
      this.unsubscribe?.();
      document.removeEventListener('click', this.documentClickHandler);
      this.uploadObserver?.disconnect();
      this.mainButtonObserver?.disconnect();
      this.uploadSlot?.removeEventListener('change', this.uploadChangeHandler, true);
      this.uploadSlot?.removeEventListener('input', this.uploadChangeHandler, true);
      this.form?.removeEventListener('submit', this.formSubmitHandler, true);
      this.form?.removeEventListener('change', this.formChangeHandler);
      this.quantityInput?.removeEventListener('input', this.quantityInputHandler);
      this.removeEventListener('rx-pd-measurement-used', this.pdMeasurementHandler);
      this.removeEventListener('rx-pd-manual-requested', this.pdManualHandler);
      this.removeEventListener('rx-pd-analytics', this.pdAnalyticsHandler);
      this.removeEventListener('rx-pd-tool-closed', this.pdToolClosedHandler);
      this.setPdToolModalState(false);
      this.pdController?.destroy?.();
      const state = privateState.get(this);
      this.unlockPageScroll();
      if (state) { state.clinical = {}; state.uploadComplete = false; state.uploadValue = ''; state.editingGroupId = ''; state.opener = null; }
      this.querySelectorAll('[data-rx-clinical]').forEach((input) => { input.value = ''; });
      privateState.delete(this);
    }
  }

  customElements.define('rx-sunglasses-builder', RxSunglassesBuilder);
})();
